import { DateTime } from 'luxon';

import { toCsv } from 'core/utils/exportCsv';

import {
  SETTINGS_MARKETING_TABS,
  SettingsMarketingTab,
} from '../Settings/settingsLinks';

import {
  IMarketingBreakdownRow,
  IMarketingChannelRuleInput,
  IMarketingChannelShare,
  IMarketingCoverageStep,
  IMarketingDeviceRow,
  IMarketingFatigue,
  IMarketingFilter,
  IMarketingFormatStat,
  IMarketingJourneyBucket,
  IMarketingMetrics,
  IMarketingPlanFact,
  IMarketingSignal,
  IMarketingSourceStatus,
  IMarketingUnmappedValue,
  MarketingAttributionModelCode,
  MarketingChannelCode,
  MarketingDate,
  MarketingDecimal,
  MarketingDimensionCode,
  MarketingGranularityCode,
  MarketingJson,
  MarketingSourceModeCode,
} from './types';

/**
 * ТЗ 0088 — словари и чистые помощники раздела «Маркетинг».
 *
 * Модуль без React, Apollo и i18n: подписи — КЛЮЧИ `marketing.*`, переводит
 * вызывающий. Всё, что решает ЦИФРУ, считает сервер; здесь — период, спуск по
 * разрезам, цвет, формат и знак дельты.
 *
 * Главное правило раздела (принцип 5 ТЗ): **пропуск ≠ ноль**. `null` из
 * контракта значит «не знаем» — расход непокрытого дня, CPL без лидов. Ни
 * один помощник здесь не превращает `null` в `0`: форматтеры рисуют «—»,
 * дельта не считается, доля не делится.
 */

/* ── Вкладки ─────────────────────────────────────────────────────────── */

/**
 * Вкладки раздела (ТЗ 0103 — новая структура: вопрос на вкладку). Чистый
 * `/marketing` открывает «Сегодня». Справочники — в «Настройки → Маркетинг»
 * (ТЗ 0093), операционка (источники, план, журнал выгрузки) — вкладка `data`.
 * Старые id (`pulse`, `channels`, `creatives`, `search`) живут только в
 * старых ссылках и уводятся `marketingLegacyTabTarget` до любого запроса.
 */
export const MARKETING_ALL_TABS = [
  'today',
  'leads',
  'ads',
  'courses',
  'journey',
  'seo',
  'digest',
  'data',
] as const;

export type MarketingTabId = typeof MARKETING_ALL_TABS[number];

/** Вкладки полосы — в её порядке. */
export const MARKETING_TABS: MarketingTabId[] = [...MARKETING_ALL_TABS];

export const MARKETING_DEFAULT_TAB: MarketingTabId = 'today';

export const isMarketingTab = (
  value: string | null | undefined,
): value is MarketingTabId =>
  Boolean(value) && (MARKETING_TABS as string[]).includes(value as string);

/**
 * Вкладки до ТЗ 0103 — в задачах сервера, закладках и ссылках коллегам.
 * На экран не попадают: адрес с ними страница переписывает
 * (`marketingLegacyLinks.ts`).
 */
export const MARKETING_LEGACY_TABS = [
  'pulse',
  'channels',
  'creatives',
  'search',
  // Повторное ревью 0103, пакет 2 (P4): «Качество данных» — подвкладки
  // «Данных» (`dtab=quality|mapping|sales`).
  'quality',
] as const;

export type MarketingLegacyTabId = typeof MARKETING_LEGACY_TABS[number];

/**
 * Вкладки с рядами по времени — у них «Шаг» (design-erp §2): «Лиды и
 * продажи» (графики, периоды, когорты), «Реклама» (ряды панели справа),
 * «Курсы» (малые графики), «Путь клиента» (ряды посетителей и страниц —
 * с их бэкендом).
 */
export const MARKETING_STEP_TABS: MarketingTabId[] = [
  'leads',
  'ads',
  'courses',
  'journey',
];

/**
 * Вкладки, где действует «Сравнить» (окно считает сервер — `compare`):
 * KPI, графики и периоды «Лидов», колонки сравнения «Рекламы» и «Курсов»,
 * «Путь клиента», итоги «было» SEO › поисковиков.
 */
export const MARKETING_COMPARE_TABS: MarketingTabId[] = [
  'leads',
  'ads',
  'courses',
  'journey',
  'seo',
];

/* ── Период ──────────────────────────────────────────────────────────── */

export const MARKETING_PERIOD_PRESETS = [
  '7d',
  '30d',
  '90d',
  // ТЗ 0103: ISO-недели — эта (с понедельника по сегодня) и прошлая целиком.
  'thisWeek',
  'prevWeek',
  'thisMonth',
  'prevMonth',
  'quarter',
  'year',
  'custom',
] as const;

export type MarketingPeriodPreset = typeof MARKETING_PERIOD_PRESETS[number];

/**
 * ТЗ 0103, доводка первого экрана: частые периоды — сегментами на виду,
 * остальные — в «Ещё ▾» (вместе — все `MARKETING_PERIOD_PRESETS`).
 */
export const MARKETING_MAIN_PERIOD_PRESETS: ReadonlyArray<MarketingPeriodPreset> = [
  '7d',
  '30d',
  '90d',
  'thisMonth',
  'prevMonth',
];

export const MARKETING_MORE_PERIOD_PRESETS: ReadonlyArray<MarketingPeriodPreset> = MARKETING_PERIOD_PRESETS.filter(
  (preset) => !MARKETING_MAIN_PERIOD_PRESETS.includes(preset),
);

/** Быстрый период с готовыми границами (без «своего»). */
export type MarketingFixedPreset = Exclude<MarketingPeriodPreset, 'custom'>;

export const MARKETING_DEFAULT_PRESET: MarketingFixedPreset = '30d';

export const isMarketingPreset = (
  value: string | null | undefined,
): value is MarketingPeriodPreset =>
  Boolean(value) &&
  (MARKETING_PERIOD_PRESETS as readonly string[]).includes(value as string);

/** Окно дат ВКЛЮЧИТЕЛЬНО, ISO-днями по Варшаве. */
export interface IMarketingRange {
  from: MarketingDate;
  to: MarketingDate;
}

/** Контракт: `dateTo - dateFrom ≤ 400` дней. */
export const MARKETING_MAX_SPAN_DAYS = 400;

/** Контракт: при окне длиннее 92 дней гранулярность `DAY` запрещена. */
export const MARKETING_DAY_GRANULARITY_MAX_DAYS = 92;

export const MARKETING_WARSAW_TZ = 'Europe/Warsaw';

const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Календарная арифметика — в UTC: «плюс день» у полуночи перехода на летнее
 * время в зоне давал бы 23 или 25 часов, и день мог съехать.
 */
const calendarDay = (iso: string): DateTime =>
  DateTime.fromISO(iso, { zone: 'utc' });

const isoOf = (value: DateTime): MarketingDate => value.toISODate();

export const isMarketingIsoDay = (value: string | null | undefined): boolean =>
  Boolean(value) &&
  ISO_DAY_RE.test(value as string) &&
  calendarDay(value as string).isValid;

/**
 * «Сегодня» по Варшаве (ТЗ, определение «День»). Не по часам браузера:
 * у менеджера в поездке или у агентства в другом поясе после полуночи по
 * Варшаве окно «7 дней» иначе съезжало бы на день.
 */
export const marketingTodayWarsaw = (now: Date = new Date()): MarketingDate =>
  DateTime.fromJSDate(now).setZone(MARKETING_WARSAW_TZ).toISODate();

/**
 * Границы быстрого периода. «7/30/90 дней» — сегодня и N−1 дней до него;
 * «эта неделя / месяц / квартал / год» — с первого дня (неделя — с
 * понедельника, ISO) по сегодня; «прошлая неделя / прошлый месяц» —
 * целиком. `custom` здесь не считается — его даты задаёт человек.
 */
export const marketingPresetRange = (
  preset: MarketingFixedPreset,
  today: MarketingDate,
): IMarketingRange => {
  const end = calendarDay(today);
  const to = isoOf(end);
  switch (preset) {
    case '7d':
      return { from: isoOf(end.minus({ days: 6 })), to };
    case '30d':
      return { from: isoOf(end.minus({ days: 29 })), to };
    case '90d':
      return { from: isoOf(end.minus({ days: 89 })), to };
    case 'thisWeek':
      return { from: isoOf(end.startOf('week')), to };
    case 'prevWeek': {
      const start = end.startOf('week').minus({ weeks: 1 });
      return { from: isoOf(start), to: isoOf(start.endOf('week')) };
    }
    case 'thisMonth':
      return { from: isoOf(end.startOf('month')), to };
    case 'prevMonth': {
      const start = end.startOf('month').minus({ months: 1 });
      return { from: isoOf(start), to: isoOf(start.endOf('month')) };
    }
    case 'quarter':
      return { from: isoOf(end.startOf('quarter')), to };
    case 'year':
    default:
      return { from: isoOf(end.startOf('year')), to };
  }
};

/** День со сдвигом: `2026-09-28`, −1 → `2026-09-27`. */
export const marketingShiftDay = (
  day: MarketingDate,
  delta: number,
): MarketingDate => isoOf(calendarDay(day).plus({ days: delta }));

/** Длина окна в днях, включая оба края. */
export const marketingRangeDays = (range: IMarketingRange): number =>
  Math.round(calendarDay(range.to).diff(calendarDay(range.from), 'days').days) +
  1;

/** Окно той же длины сразу перед текущим — «прошлый период» у дельт. */
export const marketingPreviousRange = (
  range: IMarketingRange,
): IMarketingRange => {
  const length = marketingRangeDays(range);
  const to = calendarDay(range.from).minus({ days: 1 });
  return { from: isoOf(to.minus({ days: length - 1 })), to: isoOf(to) };
};

export type MarketingRangeIssue = 'invalid' | 'order' | 'tooLong';

/** Что не так со своим периодом; `null` — можно запрашивать. */
export const marketingRangeIssue = (
  range: Partial<IMarketingRange> | null | undefined,
): MarketingRangeIssue | null => {
  if (
    !range ||
    !isMarketingIsoDay(range.from) ||
    !isMarketingIsoDay(range.to)
  ) {
    return 'invalid';
  }
  const full = range as IMarketingRange;
  const span = marketingRangeDays(full) - 1;
  if (span < 0) return 'order';
  if (span > MARKETING_MAX_SPAN_DAYS) return 'tooLong';
  return null;
};

export const MARKETING_GRANULARITIES: MarketingGranularityCode[] = [
  'DAY',
  'WEEK',
  'MONTH',
];

/** Шаги ряда, которые сервер примет для этого окна. */
export const marketingGranularities = (
  range: IMarketingRange,
): MarketingGranularityCode[] =>
  marketingRangeDays(range) > MARKETING_DAY_GRANULARITY_MAX_DAYS
    ? MARKETING_GRANULARITIES.filter((step) => step !== 'DAY')
    : MARKETING_GRANULARITIES;

/** Недопустимый для окна шаг заменяется неделей (шаг ТЗ по умолчанию). */
export const marketingSafeGranularity = (
  granularity: MarketingGranularityCode,
  range: IMarketingRange,
): MarketingGranularityCode =>
  marketingGranularities(range).includes(granularity) ? granularity : 'WEEK';

/**
 * Подпись точки ряда: день `2026-09-14` → «14.09», неделя `2026-W38` →
 * понедельник «14.09», месяц `2026-09` → «09.2026». Не текущий год у дня и
 * недели — «14.09.25», иначе за год две одинаковые подписи. Незнакомое — как
 * пришло.
 */
export const marketingPeriodLabel = (
  period: string,
  currentYear: number = new Date().getFullYear(),
): string => {
  const short = (value: DateTime) =>
    value.toFormat(value.year === currentYear ? 'dd.LL' : 'dd.LL.yy');
  if (ISO_DAY_RE.test(period)) {
    const value = calendarDay(period);
    return value.isValid ? short(value) : period;
  }
  const week = /^(\d{4})-W(\d{2})$/.exec(period);
  if (week) {
    const monday = DateTime.fromISO(`${week[1]}-W${week[2]}-1`, {
      zone: 'utc',
    });
    return monday.isValid ? short(monday) : period;
  }
  const month = /^(\d{4})-(\d{2})$/.exec(period);
  if (month) return `${month[2]}.${month[1]}`;
  return period;
};

/**
 * Шаг ряда по ключу точки сервера: `2026-09-14` — день, `2026-W38` —
 * неделя, `2026-09` — месяц; незнакомое — `null`. Заголовок графика берёт
 * шаг из самих точек (повторное ревью 0103, A18: «по неделям» над дневной
 * осью).
 */
export const marketingPeriodGranularity = (
  period: string | null | undefined,
): MarketingGranularityCode | null => {
  if (!period) return null;
  if (ISO_DAY_RE.test(period)) return 'DAY';
  if (/^\d{4}-W\d{2}$/.test(period)) return 'WEEK';
  if (/^\d{4}-\d{2}$/.test(period)) return 'MONTH';
  return null;
};

const AD_URL_RE = /^(?:https?:\/\/)?(?:www\.)?([a-z0-9.-]+\.[a-z]{2,})(\/[^\s?#]*)?(?:[?#]\S*)?$/i;

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/**
 * Повторное ревью 0103 (A20): имя объявления Google RSA приходит адресом
 * страницы («https://learnit.com.pl/kursy/python-developer»), и после
 * усечения строки неразличимы. Показываем «RSA · <последний сегмент пути>»
 * (без пути — хост); полное имя — в подсказке. Не адрес — как есть.
 */
export const marketingAdLabel = (name: string | null | undefined): string => {
  const raw = (name ?? '').trim();
  const match = AD_URL_RE.exec(raw);
  const urlLike =
    match && (/^(https?:\/\/|www\.)/i.test(raw) || Boolean(match[2]));
  if (!match || !urlLike) return name ?? '';
  const segments = (match[2] ?? '').split('/').filter(Boolean);
  const last = segments.length
    ? safeDecode(segments[segments.length - 1])
    : match[1].toLowerCase();
  return `RSA · ${last}`;
};

/** День полностью: `2026-06-15` → «15.06.2026»; мусор — как пришёл. */
export const marketingDayLabel = (day: string): string => {
  if (!ISO_DAY_RE.test(day)) return day;
  const value = calendarDay(day);
  return value.isValid ? value.toFormat('dd.LL.yyyy') : day;
};

/** «14.09» или «14.09–20.09» — окно одной строкой. */
export const marketingRangeLabel = (
  range: IMarketingRange,
  currentYear: number = new Date().getFullYear(),
): string => {
  const from = marketingPeriodLabel(range.from, currentYear);
  const to = marketingPeriodLabel(range.to, currentYear);
  return from === to ? from : `${from}–${to}`;
};

/**
 * Непокрытые дни расхода — отрезками подряд идущих дней: «03.09–05.09,
 * 12.09» читается, а тридцать дат подряд — нет. Повторы и порядок входа не
 * важны.
 */
export const marketingDayRanges = (
  days: ReadonlyArray<MarketingDate>,
): IMarketingRange[] => {
  const sorted = Array.from(new Set(days.filter(isMarketingIsoDay))).sort();
  return sorted.reduce<IMarketingRange[]>((ranges, day) => {
    const last = ranges[ranges.length - 1];
    if (last && isoOf(calendarDay(last.to).plus({ days: 1 })) === day) {
      return [...ranges.slice(0, -1), { from: last.from, to: day }];
    }
    return [...ranges, { from: day, to: day }];
  }, []);
};

/** Момент источника по Варшаве: «28.09 14:05». */
export const marketingStamp = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const value = DateTime.fromISO(iso).setZone(MARKETING_WARSAW_TZ);
  return value.isValid ? value.toFormat('dd.LL HH:mm') : '';
};

/**
 * Только день момента по Варшаве («25.09»): время события округлено до дня
 * (`roundedToDay` — лид, договор и продажа у всех, кроме Admin), и «00:00»
 * читалось бы как настоящее время.
 */
export const marketingDayStamp = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const value = DateTime.fromISO(iso).setZone(MARKETING_WARSAW_TZ);
  return value.isValid ? value.toFormat('dd.LL') : '';
};

/* ── Каналы ──────────────────────────────────────────────────────────── */

export const MARKETING_CHANNELS: MarketingChannelCode[] = [
  'META',
  'GOOGLE_ADS',
  'SEO_GOOGLE',
  'SEO_BING',
  'AI_REFERRAL',
  'EMAIL',
  'DIRECT',
  'REFERRAL',
  'OTHER_PAID',
  'PHONE',
  'UNKNOWN',
];

/** Каналы, у которых есть расход (и, значит, CPL/ROAS). */
export const MARKETING_PAID_CHANNELS: MarketingChannelCode[] = [
  'META',
  'GOOGLE_ADS',
  'OTHER_PAID',
];

/**
 * Цвет — за КАНАЛОМ, а не за местом в ряду (как `SERIES_COLORS` аналитики
 * договоров): фильтр, убравший часть каналов, не перекрашивает оставшиеся, и
 * Meta синяя в плитке, в долях и на графике строки.
 */
export const MARKETING_CHANNEL_COLORS: Readonly<
  Record<MarketingChannelCode, string>
> = {
  META: '#2a78d6',
  GOOGLE_ADS: '#eb6834',
  SEO_GOOGLE: '#1baf7a',
  SEO_BING: '#147d79',
  AI_REFERRAL: '#4a3aa7',
  EMAIL: '#eda100',
  DIRECT: '#64748b',
  REFERRAL: '#e87ba4',
  OTHER_PAID: '#a17d2c',
  // Этап 2: звонок (источник лида Kommo «Zadarma» без меток).
  PHONE: '#0891b2',
  UNKNOWN: '#94a3b8',
};

/** Код, которого нет в словаре (сервер добавил канал раньше фронта). */
export const MARKETING_FALLBACK_COLOR = '#cbd5e1';

export const isMarketingChannel = (
  value: string | null | undefined,
): value is MarketingChannelCode =>
  Boolean(value) && (MARKETING_CHANNELS as string[]).includes(value as string);

export const marketingChannelColor = (
  code: string | null | undefined,
): string =>
  isMarketingChannel(code)
    ? MARKETING_CHANNEL_COLORS[code]
    : MARKETING_FALLBACK_COLOR;

/**
 * Код канала из ключа строки разреза. Таблица `CHANNEL` отдаёт ключ кодом
 * базы (`meta`), а enum контракта и фильтр — `META`: спуск, подпись и цвет
 * работают только с кодом enum. Незнакомый ключ — как есть.
 */
export const marketingChannelFromKey = (key: string): string => {
  const upper = key.toUpperCase();
  return isMarketingChannel(upper) ? upper : key;
};

/**
 * Код канала базы (`meta`, `google_ads`) из кода enum (`META`): в нём
 * хранятся строки плана и площадки креативов.
 */
export const marketingChannelDbCode = (code: string): string =>
  code.toLowerCase();

/**
 * Ключ подписи канала; `null` — кода нет в словаре, экран покажет код как
 * есть (а не спрячет новый канал под «неизвестным»).
 */
export const marketingChannelLabelKey = (
  code: string | null | undefined,
): string | null =>
  isMarketingChannel(code) ? `marketing.channels.${code}` : null;

/* ── Разрезы и спуск ─────────────────────────────────────────────────── */

export const MARKETING_DIMENSIONS: MarketingDimensionCode[] = [
  'CHANNEL',
  'CAMPAIGN',
  'ADSET',
  'AD',
  'FAMILY',
  'OFFER',
  'FORMAT',
];

/**
 * С чего начинается таблица «Рекламы»: «Группировать» (ТЗ 0103 — каналы со
 * спуском, кампании, объявления, курсы, предложения). Кампании без канала
 * сервер отдаёт корнем без `parentKey`; объявления рисует таблица
 * «Креативов» (`marketingCreatives`), а не разрез.
 */
export const MARKETING_ROOT_DIMENSIONS: MarketingDimensionCode[] = [
  'CHANNEL',
  'CAMPAIGN',
  'AD',
  'FAMILY',
  'OFFER',
  // ТЗ 0103 §A5: объявления, сложенные по формату (`AdEntity.format`).
  'FORMAT',
];

export const MARKETING_DEFAULT_DIMENSION: MarketingDimensionCode = 'CHANNEL';

/**
 * Клик по строке спускает на уровень ниже (по образцу `DRILLDOWN_NEXT`
 * опросов): канал → кампания → группа → объявление. Курс и оффер начинают
 * тот же путь с канала внутри себя. У объявления продолжения нет.
 */
export const MARKETING_DRILLDOWN_NEXT: Partial<
  Record<MarketingDimensionCode, MarketingDimensionCode>
> = {
  CHANNEL: 'CAMPAIGN',
  CAMPAIGN: 'ADSET',
  ADSET: 'AD',
  FAMILY: 'CHANNEL',
  OFFER: 'CHANNEL',
};

/** Поле фильтра, в которое ложится ключ строки при спуске. */
export const MARKETING_DRILLDOWN_FILTER_KEY: Readonly<
  Record<
    MarketingDimensionCode,
    'channels' | 'campaignIds' | 'adsetIds' | 'familySlugs' | 'offers' | null
  >
> = {
  CHANNEL: 'channels',
  CAMPAIGN: 'campaignIds',
  ADSET: 'adsetIds',
  AD: null,
  FAMILY: 'familySlugs',
  OFFER: 'offers',
  // Формат — не поле фильтра: спуска и ряда строки нет.
  FORMAT: null,
};

/**
 * Уровни рекламной цепочки, чей ключ сервер понимает как `parentKey`
 * (код канала, id кампании, id группы). У курса и оффера родителя в этом
 * смысле нет — их ключ едет только сужением фильтра.
 */
const PARENT_KEY_DIMENSIONS: MarketingDimensionCode[] = [
  'CHANNEL',
  'CAMPAIGN',
  'ADSET',
];

/**
 * Ключ строки «не определено»: у продажи нет курса, у касания — кампании или
 * оффера. Семейство и оффер `none` сервер понимает в фильтре; id кампании и
 * группы — нет (там нужны числа), поэтому в такую строку не спускаются.
 */
export const MARKETING_NONE_KEY = 'none';

const NUMERIC_KEY_DIMENSIONS: MarketingDimensionCode[] = ['CAMPAIGN', 'ADSET'];

export const isMarketingRootDimension = (
  value: string | null | undefined,
): value is MarketingDimensionCode =>
  Boolean(value) &&
  (MARKETING_ROOT_DIMENSIONS as string[]).includes(value as string);

export const marketingCanDrill = (dimension: MarketingDimensionCode): boolean =>
  Boolean(MARKETING_DRILLDOWN_NEXT[dimension]);

/**
 * Можно ли спуститься в ЭТУ строку: у разреза есть следующий уровень, и ключ
 * строки годится в фильтр (кампания «не определено» — нет).
 */
export const marketingCanDrillRow = (
  dimension: MarketingDimensionCode,
  key: string,
): boolean =>
  marketingCanDrill(dimension) &&
  Boolean(key) &&
  !(NUMERIC_KEY_DIMENSIONS.includes(dimension) && key === MARKETING_NONE_KEY);

export interface IMarketingDrillStep {
  /** Разрез, в котором кликнули строку. */
  dimension: MarketingDimensionCode;
  /** Ключ этой строки. */
  key: string;
}

export interface IMarketingDrillState {
  steps: IMarketingDrillStep[];
  /** Разрез, который таблица показывает сейчас. */
  dimension: MarketingDimensionCode;
}

/**
 * Путь спуска из адреса: корень + ключи кликнутых строк. Ключи сверх того,
 * куда можно спуститься, отбрасываются — ссылка с лишним хвостом не ломает
 * экран.
 */
export const marketingDrillState = (
  root: MarketingDimensionCode,
  path: ReadonlyArray<string>,
): IMarketingDrillState => {
  const steps: IMarketingDrillStep[] = [];
  let dimension = root;
  // Первый пустой ключ или тупик разреза обрывают путь: всё после него
  // относилось бы к уровню, которого на экране нет.
  path.some((key) => {
    const next = MARKETING_DRILLDOWN_NEXT[dimension];
    if (!next || !marketingCanDrillRow(dimension, key)) return true;
    steps.push({ dimension, key });
    dimension = next;
    return false;
  });
  return { steps, dimension };
};

export interface IMarketingBreakdownVariables {
  filter: IMarketingFilter;
  dimension: MarketingDimensionCode;
  parentKey: string | null;
}

/**
 * Переменные `marketingBreakdown` для текущего спуска: общий фильтр,
 * СУЖЕННЫЙ ключами пройденных строк (канал → `channels`, кампания →
 * `campaignIds`…), разрез и `parentKey` последнего шага рекламной цепочки.
 *
 * Сужение — главный носитель: `parentKey` один, а путь «курс → канал →
 * кампания» несёт два ограничения сразу.
 */
export const marketingBreakdownVariables = (
  filter: IMarketingFilter,
  root: MarketingDimensionCode,
  path: ReadonlyArray<string>,
): IMarketingBreakdownVariables => {
  const { steps, dimension } = marketingDrillState(root, path);
  const narrowed = steps.reduce<IMarketingFilter>((acc, step) => {
    const field = MARKETING_DRILLDOWN_FILTER_KEY[step.dimension];
    if (field === 'channels') {
      return isMarketingChannel(step.key)
        ? { ...acc, channels: [step.key] }
        : acc;
    }
    return field ? { ...acc, [field]: [step.key] } : acc;
  }, filter);
  const last = steps[steps.length - 1];
  return {
    filter: narrowed,
    dimension,
    parentKey:
      last && PARENT_KEY_DIMENSIONS.includes(last.dimension) ? last.key : null,
  };
};

/**
 * Фильтр ряда ОДНОЙ строки разреза: фильтр таблицы, суженный ключом строки
 * (канал → `channels`, кампания → `campaignIds`…). `null` — строку сузить
 * нечем: у объявления нет поля фильтра, у кампании «не определено» — числа.
 * Тогда ряд берётся из самой таблицы (`withSeries`).
 */
export const marketingRowSeriesFilter = (
  filter: IMarketingFilter,
  dimension: MarketingDimensionCode,
  key: string,
): IMarketingFilter | null => {
  const field = MARKETING_DRILLDOWN_FILTER_KEY[dimension];
  if (!field || !key) return null;
  if (NUMERIC_KEY_DIMENSIONS.includes(dimension) && key === MARKETING_NONE_KEY)
    return null;
  if (field === 'channels') {
    return isMarketingChannel(key) ? { ...filter, channels: [key] } : null;
  }
  return { ...filter, [field]: [key] };
};

/**
 * Подписи ключей пройденного пути: имена с сервера (`breakdown.crumbs` —
 * кампании и группы из фильтра спуска) и то, что человек видел при клике.
 * Клик главнее: он подписал ключ тем же текстом, что был в строке. Сервер
 * заполняет остальное — свежую ссылку, перезагрузку, «Назад» из другой
 * вкладки, где в адресе только id.
 */
export const marketingCrumbLabels = (
  serverCrumbs: ReadonlyArray<{ id: string; name: string }> | null | undefined,
  clicked: Readonly<Record<string, string>>,
): Record<string, string> => ({
  ...(serverCrumbs ?? []).reduce<Record<string, string>>(
    (acc, crumb) => (crumb.name ? { ...acc, [crumb.id]: crumb.name } : acc),
    {},
  ),
  ...clicked,
});

/* ── Шаг ряда по длине окна ──────────────────────────────────────────── */

/** До месяца включительно — по дням: неделя окна «7 дней» была бы одной точкой. */
export const MARKETING_AUTO_DAY_MAX_DAYS = 31;

/** Дольше полугода — по месяцам: 52 недельные точки не читаются. */
export const MARKETING_AUTO_WEEK_MAX_DAYS = 183;

/**
 * Шаг ряда для окна: короткое — по дням, среднее — по неделям (шаг ТЗ),
 * длинное — по месяцам. Всегда из тех, что сервер примет.
 */
export const marketingAutoGranularity = (
  range: IMarketingRange,
): MarketingGranularityCode => {
  const days = marketingRangeDays(range);
  if (days <= MARKETING_AUTO_DAY_MAX_DAYS) return 'DAY';
  if (days <= MARKETING_AUTO_WEEK_MAX_DAYS) return 'WEEK';
  return 'MONTH';
};

/* ── Модели атрибуции ────────────────────────────────────────────────── */

export const MARKETING_ATTRIBUTION_MODELS: MarketingAttributionModelCode[] = [
  'LAST',
  'FIRST',
  'LINEAR',
  'PARTICIPATION',
];

export const MARKETING_DEFAULT_MODEL: MarketingAttributionModelCode = 'LAST';

export const isMarketingModel = (
  value: string | null | undefined,
): value is MarketingAttributionModelCode =>
  Boolean(value) &&
  (MARKETING_ATTRIBUTION_MODELS as string[]).includes(value as string);

/**
 * `participation` отдаёт продажу каждому каналу целиком — сумма по каналам
 * больше итога. Экран обязан это сказать, иначе строки «не сходятся».
 */
export const marketingModelSumsToTotal = (
  model: MarketingAttributionModelCode,
): boolean => model !== 'PARTICIPATION';

/* ── Покрытие продаж ─────────────────────────────────────────────────── */

/** Глубина, до которой известна продажа: none < lead < channel < campaign < ad. */
export const MARKETING_COVERAGE_LEVELS = [
  'none',
  'lead',
  'channel',
  'campaign',
  'ad',
] as const;

export type MarketingCoverageLevel = typeof MARKETING_COVERAGE_LEVELS[number];

/** Место уровня в шкале; `-1` — код не из словаря. */
export const marketingCoverageRank = (
  level: string | null | undefined,
): number =>
  level ? (MARKETING_COVERAGE_LEVELS as readonly string[]).indexOf(level) : -1;

export const marketingCoverageAtLeast = (
  level: string | null | undefined,
  min: MarketingCoverageLevel,
): boolean =>
  marketingCoverageRank(level) >= 0 &&
  marketingCoverageRank(level) >= marketingCoverageRank(min);

/**
 * Тон покрытия продажи: до кампании (и глубже) — хорошо, до лида или канала
 * — справочно, без лида — предупреждение. Один и тот же в таблице продаж и
 * в панели продажи справа.
 */
export const marketingSaleCoverageTone = (
  level: string | null | undefined,
): 'ok' | 'info' | 'warn' => {
  if (marketingCoverageAtLeast(level, 'campaign')) return 'ok';
  if (marketingCoverageAtLeast(level, 'lead')) return 'info';
  return 'warn';
};

/**
 * Ступени воронки покрытия в порядке шкалы; незнакомые уровни — в хвосте,
 * чтобы новое с сервера было видно, а не пропало.
 */
export const marketingCoverageFunnel = (
  steps: ReadonlyArray<IMarketingCoverageStep>,
): IMarketingCoverageStep[] =>
  [...steps].sort((a, b) => {
    const rankA = marketingCoverageRank(a.level);
    const rankB = marketingCoverageRank(b.level);
    return (
      (rankA < 0 ? MARKETING_COVERAGE_LEVELS.length : rankA) -
      (rankB < 0 ? MARKETING_COVERAGE_LEVELS.length : rankB)
    );
  });

/* ── Числа и форматы ─────────────────────────────────────────────────── */

export const MARKETING_DASH = '—';

/**
 * Число из Decimal-строки или Int/Float контракта. Пусто, `null`, мусор —
 * `null`, а не `0`.
 */
export const marketingNumber = (
  value: MarketingDecimal | number | null | undefined,
): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Язык интерфейса → локаль чисел: польский — «12 345 zł», иначе «12 345 PLN». */
const localeOf = (lang?: string | null): string =>
  lang && lang.toLowerCase().startsWith('pl') ? 'pl-PL' : 'ru-RU';

/**
 * Форматтеры кэшируются: `Intl.NumberFormat` дорог, а таблица каналов — это
 * 15 колонок на каждую строку (урок `CashFlowReport/shared.ts`).
 */
const FORMATTERS = new Map<string, Intl.NumberFormat>();

const formatter = (
  name: string,
  lang: string | null | undefined,
  options: Intl.NumberFormatOptions,
): Intl.NumberFormat => {
  const locale = localeOf(lang);
  const cacheKey = `${locale}:${name}`;
  const cached = FORMATTERS.get(cacheKey);
  if (cached) return cached;
  const created = new Intl.NumberFormat(locale, options);
  FORMATTERS.set(cacheKey, created);
  return created;
};

type MarketingNumeric = MarketingDecimal | number | null | undefined;

/** Деньги в злотых целыми; `precise` — с грошами (CPC, CPM). */
export const formatMarketingMoney = (
  value: MarketingNumeric,
  lang?: string | null,
  precise = false,
): string => {
  const number = marketingNumber(value);
  if (number === null) return MARKETING_DASH;
  const digits = precise ? 2 : 0;
  return formatter(precise ? 'moneyPrecise' : 'money', lang, {
    style: 'currency',
    currency: 'PLN',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(number);
};

/**
 * ТЗ 0103: доллары DataForSEO (бюджет и сметы) — «$0,66»; `precise` — до
 * четырёх знаков (цена позиции сметы бывает в долях цента).
 */
export const formatMarketingUsd = (
  value: MarketingNumeric,
  lang?: string | null,
  precise = false,
): string => {
  const number = marketingNumber(value);
  if (number === null) return MARKETING_DASH;
  return formatter(precise ? 'usdPrecise' : 'usd', lang, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: precise ? 4 : 2,
  }).format(number);
};

/** Счётчик: показы, клики, лиды. */
export const formatMarketingCount = (
  value: MarketingNumeric,
  lang?: string | null,
): string => {
  const number = marketingNumber(value);
  if (number === null) return MARKETING_DASH;
  return formatter('count', lang, { maximumFractionDigits: 0 }).format(number);
};

/** Продажи: модель `LINEAR` даёт дроби — до одного знака после запятой. */
export const formatMarketingSales = (
  value: MarketingNumeric,
  lang?: string | null,
): string => {
  const number = marketingNumber(value);
  if (number === null) return MARKETING_DASH;
  return formatter('sales', lang, { maximumFractionDigits: 1 }).format(number);
};

/** Доля (0..1) процентами: CTR, конверсия лид → продажа, доля канала. */
export const formatMarketingPct = (
  value: MarketingNumeric,
  lang?: string | null,
): string => {
  const number = marketingNumber(value);
  if (number === null) return MARKETING_DASH;
  return formatter('pct', lang, {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(number);
};

/**
 * Малая доля — до двух знаков: CTR, hook / hold rate. «1,1 % против 1,6 %»
 * прятало бы разницу, ради которой CTR и смотрят («1,06 % против 1,63 %»).
 */
export const formatMarketingRate = (
  value: MarketingNumeric,
  lang?: string | null,
): string => {
  const number = marketingNumber(value);
  if (number === null) return MARKETING_DASH;
  return formatter('rate', lang, {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(number);
};

/** ROAS — «во сколько раз вернулось»: «2,35×». */
export const formatMarketingRatio = (
  value: MarketingNumeric,
  lang?: string | null,
): string => {
  const number = marketingNumber(value);
  if (number === null) return MARKETING_DASH;
  return `${formatter('ratio', lang, { maximumFractionDigits: 2 }).format(
    number,
  )}×`;
};

/**
 * Средняя позиция в поиске — «14,2»: десятые (позиция взвешена показами,
 * целое прятало бы сдвиг, а сотые — ложная точность).
 */
export const formatMarketingPosition = (
  value: MarketingNumeric,
  lang?: string | null,
): string => {
  const number = marketingNumber(value);
  if (number === null) return MARKETING_DASH;
  return formatter('position', lang, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(number);
};

/**
 * Разница со знаком: «+18», «−3», «0» (у позиции — десятые: «−1,9»). Минус —
 * типографский, как у дельт плиток.
 */
export const formatMarketingSigned = (
  value: MarketingNumeric,
  lang?: string | null,
  fractionDigits = 0,
): string => {
  const number = marketingNumber(value);
  if (number === null) return MARKETING_DASH;
  const scale = 10 ** fractionDigits;
  const rounded = Math.round(Math.abs(number) * scale) / scale;
  const body = formatter(`signed${fractionDigits}`, lang, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(rounded);
  if (rounded === 0) return body;
  return `${number > 0 ? '+' : '−'}${body}`;
};

export type MarketingValueFormat =
  | 'money'
  | 'moneyPrecise'
  | 'count'
  | 'sales'
  | 'pct'
  | 'rate'
  | 'ratio';

export const formatMarketingValue = (
  format: MarketingValueFormat,
  value: MarketingNumeric,
  lang?: string | null,
): string => {
  switch (format) {
    case 'money':
      return formatMarketingMoney(value, lang);
    case 'moneyPrecise':
      return formatMarketingMoney(value, lang, true);
    case 'count':
      return formatMarketingCount(value, lang);
    case 'sales':
      return formatMarketingSales(value, lang);
    case 'pct':
      return formatMarketingPct(value, lang);
    case 'rate':
      return formatMarketingRate(value, lang);
    case 'ratio':
    default:
      return formatMarketingRatio(value, lang);
  }
};

/* ── Показатели ──────────────────────────────────────────────────────── */

/**
 * Показатель плиток и колонок. Частичный расход (`spendCovered*`) и
 * отношения за собранные дни (`*Covered`, `ratiosThrough`) — не показатели:
 * это способ показать `spendPln` и отношения, когда окно собрано не целиком
 * (`marketingSpendView`, `marketingRatioView`). Повторные заявки
 * (`crmLeadsRepeat`) — подпись к CRM-лидам, а не своя колонка.
 */
export type MarketingMetricKey = Exclude<
  keyof IMarketingMetrics,
  | 'spendCoveredPln'
  | 'spendCoveredThrough'
  | 'crmLeadsRepeat'
  | 'ratiosThrough'
  | 'cplCoveredPln'
  | 'costPerSaleCoveredPln'
  | 'roasCovered'
  | 'roasCashCovered'
>;

/** Куда «лучше»: вверх, вниз (затраты) или никуда (расход сам по себе). */
export type MarketingMetricDirection = 'up' | 'down' | 'neutral';

export interface IMarketingMetricDef {
  key: MarketingMetricKey;
  format: MarketingValueFormat;
  direction: MarketingMetricDirection;
}

/**
 * Словарь показателей. `down` — затратные: рост CPL и стоимости продажи —
 * плохо. Расход `neutral`: больше денег в рекламе не хорошо и не плохо само по
 * себе — оценивается тем, что они принесли.
 */
export const MARKETING_METRICS: Readonly<
  Record<MarketingMetricKey, IMarketingMetricDef>
> = {
  spendPln: { key: 'spendPln', format: 'money', direction: 'neutral' },
  impressions: { key: 'impressions', format: 'count', direction: 'up' },
  clicks: { key: 'clicks', format: 'count', direction: 'up' },
  ctr: { key: 'ctr', format: 'rate', direction: 'up' },
  cpc: { key: 'cpc', format: 'moneyPrecise', direction: 'down' },
  cpm: { key: 'cpm', format: 'moneyPrecise', direction: 'down' },
  leadsPlatform: { key: 'leadsPlatform', format: 'count', direction: 'up' },
  crmLeads: { key: 'crmLeads', format: 'count', direction: 'up' },
  touchesOther: { key: 'touchesOther', format: 'count', direction: 'up' },
  sales: { key: 'sales', format: 'sales', direction: 'up' },
  revenuePln: { key: 'revenuePln', format: 'money', direction: 'up' },
  cashPln: { key: 'cashPln', format: 'money', direction: 'up' },
  cplPln: { key: 'cplPln', format: 'money', direction: 'down' },
  costPerSalePln: { key: 'costPerSalePln', format: 'money', direction: 'down' },
  roas: { key: 'roas', format: 'ratio', direction: 'up' },
  roasCash: { key: 'roasCash', format: 'ratio', direction: 'up' },
  // ТЗ 0103: договоры и конверсии (поля сервера с этапа 2).
  contractsSent: { key: 'contractsSent', format: 'count', direction: 'up' },
  contractsSigned: { key: 'contractsSigned', format: 'count', direction: 'up' },
  contractsPending: {
    key: 'contractsPending',
    format: 'count',
    direction: 'neutral',
  },
  contractsLost: { key: 'contractsLost', format: 'count', direction: 'down' },
  contractSignRate: {
    key: 'contractSignRate',
    format: 'pct',
    direction: 'up',
  },
  costPerContractPln: {
    key: 'costPerContractPln',
    format: 'money',
    direction: 'down',
  },
  leadToSale: { key: 'leadToSale', format: 'pct', direction: 'up' },
  leadToContract: { key: 'leadToContract', format: 'pct', direction: 'up' },
};

/**
 * Восемь плиток «Лидов и продаж» (ТЗ 0103, design-erp §3.2) — в порядке
 * воронки: лиды, договоры, продажи, конверсия, деньги, расход, стоимость.
 */
export const MARKETING_LEADS_KPIS: MarketingMetricKey[] = [
  'crmLeads',
  'contractsSent',
  'sales',
  'leadToSale',
  'revenuePln',
  'cashPln',
  'spendPln',
  'costPerSalePln',
];

/**
 * Метрика главного графика «Лидов и продаж» (`lm` адреса): лиды, договоры,
 * продажи, расход. Клик по плитке KPI меняет её.
 */
export const MARKETING_LEAD_METRICS = [
  'crmLeads',
  'contractsSent',
  'sales',
  'spendPln',
] as const;

export type MarketingLeadMetric = typeof MARKETING_LEAD_METRICS[number];

export const MARKETING_DEFAULT_LEAD_METRIC: MarketingLeadMetric = 'crmLeads';

export const isMarketingLeadMetric = (
  value: string | null | undefined,
): value is MarketingLeadMetric =>
  Boolean(value) &&
  (MARKETING_LEAD_METRICS as readonly string[]).includes(value as string);

/**
 * Разбивка графика лидов и когорт (`split`): каналы, курсы, предложения,
 * вид пути лида — одиночный / многоканальный (данные —
 * `marketingLeadDynamics(groupBy)` и `marketingCohorts(splitBy)`).
 */
export const MARKETING_SPLITS = ['channel', 'family', 'offer', 'path'] as const;

export type MarketingSplit = typeof MARKETING_SPLITS[number];

export const isMarketingSplit = (
  value: string | null | undefined,
): value is MarketingSplit =>
  Boolean(value) &&
  (MARKETING_SPLITS as readonly string[]).includes(value as string);

/**
 * Метрики с производными, которые экран может посчитать сам, пока сервер
 * их не отдаёт: конверсия лид → продажа = продажи / CRM-лиды (лидов нет —
 * `null`, а не «0 %»). Пришло с сервера — берётся оно.
 */
export const marketingDerivedMetrics = (
  metrics: IMarketingMetrics,
): IMarketingMetrics => {
  if (metrics.leadToSale !== undefined && metrics.leadToSale !== null) {
    return metrics;
  }
  return {
    ...metrics,
    leadToSale: marketingConversion(metrics.sales, metrics.crmLeads),
  };
};

/** Колонки таблицы разреза — в порядке ТЗ (покрытие — отдельной колонкой). */
export const MARKETING_BREAKDOWN_COLUMNS: MarketingMetricKey[] = [
  'impressions',
  'clicks',
  'ctr',
  'cpc',
  'cpm',
  'spendPln',
  'leadsPlatform',
  'crmLeads',
  'cplPln',
  // ТЗ 0103: отправленные и подписанные договоры (эпизоды, последнее
  // касание).
  'contractsSent',
  'contractsSigned',
  'sales',
  'revenuePln',
  'cashPln',
  'costPerSalePln',
  'roas',
];

/**
 * Колонки таблицы по умолчанию (ТЗ 0103, design-erp §3.3) — путь денег по
 * воронке: расход, CRM-лиды, CPL, отправлено договоров, продажи, стоимость
 * продажи, подписано, ROAS (и покрытие рядом). Поступило, показы, клики,
 * CTR, CPC, CPM и лиды площадки — по кнопке «Все колонки».
 */
export const MARKETING_BREAKDOWN_DEFAULT_COLUMNS: MarketingMetricKey[] = [
  'spendPln',
  'crmLeads',
  'cplPln',
  'contractsSent',
  'sales',
  'costPerSalePln',
  'revenuePln',
  'roas',
];

/**
 * «Прошлый период колонками»: три ключевые метрики раскрываются в значение
 * | было | Δ (остальные — изменением под цифрой).
 */
export const MARKETING_COMPARE_COLUMNS: MarketingMetricKey[] = [
  'spendPln',
  'crmLeads',
  'sales',
];

/** `orderBy` сервера «по изменению»: `delta_<показатель>` (только со сравнением). */
export const marketingDeltaOrderKey = (key: string): string => `delta_${key}`;

/**
 * «Все колонки» — те же деньги впереди, остальные показатели ТЗ за ними в
 * порядке ТЗ: кнопка добавляет колонки, а не перетасовывает уже знакомые.
 */
const MARKETING_BREAKDOWN_ALL_COLUMNS: MarketingMetricKey[] = [
  ...MARKETING_BREAKDOWN_DEFAULT_COLUMNS,
  ...MARKETING_BREAKDOWN_COLUMNS.filter(
    (key) => !MARKETING_BREAKDOWN_DEFAULT_COLUMNS.includes(key),
  ),
];

/** Видимые колонки таблицы разреза. */
export const marketingBreakdownColumns = (all: boolean): MarketingMetricKey[] =>
  all ? MARKETING_BREAKDOWN_ALL_COLUMNS : MARKETING_BREAKDOWN_DEFAULT_COLUMNS;

/* ── Сортировка таблицы разреза (`orderBy` сервера) ──────────────────── */

/** Что сортирует сервер: показатель, подпись строки или покрытие. */
export type MarketingSortKey = MarketingMetricKey | 'label' | 'salesCoverage';

/** Сортировка любой таблицы раздела: ключ `orderBy` и направление. */
export interface IMarketingSortOf<K extends string> {
  key: K;
  desc: boolean;
}

export type IMarketingSort = IMarketingSortOf<MarketingSortKey>;

/** `orderBy` сервера: `-crmLeads` — по убыванию; `null` — порядок сервера. */
export const marketingOrderBy = (
  sort: IMarketingSortOf<string> | null,
): string | null => (sort ? `${sort.desc ? '-' : ''}${sort.key}` : null);

/**
 * Столбцы, где первый клик — по возрастанию: текст — по алфавиту, а не «с
 * конца»; позиция в поиске — сначала вершина выдачи (1 — лучшая).
 */
const ASCENDING_FIRST = [
  'label',
  'name',
  'key',
  'term',
  'position',
  'path',
  'keyword',
];

/**
 * Следующая сортировка по клику заголовка: новый столбец — сначала по
 * убыванию (больше расхода, больше лидов — наверх), подпись — по алфавиту;
 * повторный клик переворачивает, третий — снимает (порядок сервера).
 */
export const marketingNextSort = <K extends string>(
  current: IMarketingSortOf<K> | null,
  key: K,
): IMarketingSortOf<K> | null => {
  const firstDesc = !ASCENDING_FIRST.includes(key);
  if (!current || current.key !== key) return { key, desc: firstDesc };
  if (current.desc === firstDesc) return { key, desc: !firstDesc };
  return null;
};

/* ── Ошибки запросов ─────────────────────────────────────────────────── */

/**
 * Коды `extensions.code` ошибок запросов раздела. Перевод — ключом
 * `marketing.errors.<код>`; незнакомый код — общим текстом.
 */
export const MARKETING_QUERY_ERROR_CODES = [
  'window_too_long',
  'day_granularity_window',
  'limit_too_large',
  'bad_order',
  'bad_coverage',
  'bad_window',
  'bad_filter',
  'bad_dimension',
  'bad_limit',
  'bad_granularity',
  // Этап 2.
  'bad_format',
  'bad_month',
  'bad_status',
  'bad_kind',
  'bad_platform',
  // Этап 3: поисковик, зона позиций, группировка трафика.
  'bad_engine',
  'bad_zone',
  'bad_group_by',
  // Этап 4: прогноз (будущий месяц, чужое семейство), неделя снимка.
  'bad_family',
  'bad_week',
  // ТЗ 0103: фильтр объявлений не у той разбивки, сравнение, фильтр
  // уровня лида не у того отчёта, быстрый фильтр.
  'ad_filter_dimension',
  'bad_compare',
  'lead_only_filter',
  'bad_predicate',
  // ТЗ 0103, этап 4 — SEO: запрос / вопрос не найден, смета не проходит.
  'not_found',
  'seo_budget',
] as const;

export type MarketingQueryErrorCode = typeof MARKETING_QUERY_ERROR_CODES[number];

export const marketingErrorKey = (code: string | null | undefined): string =>
  code && (MARKETING_QUERY_ERROR_CODES as readonly string[]).includes(code)
    ? `marketing.errors.${code}`
    : 'marketing.errors.unknown';

/**
 * ТЗ 0103: «стоимость чего-то» (CPL, стоимость продажи, стоимость
 * договора) — деление расхода. Расхода в окне нет (0 или не пришёл) — это
 * не «0 PLN за лид», а «—» с подсказкой «нет расхода в окне».
 */
export const MARKETING_SPEND_COST_KEYS: ReadonlyArray<MarketingMetricKey> = [
  'cplPln',
  'costPerSalePln',
  'costPerContractPln',
];

/** Расход окна для проверки «расхода нет»: полный, иначе за собранные дни. */
const spendForCost = (metrics: IMarketingMetrics): number | null =>
  marketingNumber(metrics.spendPln) ?? marketingNumber(metrics.spendCoveredPln);

/** Расхода в окне нет: он пришёл и равен 0. */
export const marketingNoSpend = (
  metrics: IMarketingMetrics | null | undefined,
): boolean =>
  Boolean(metrics) && spendForCost(metrics as IMarketingMetrics) === 0;

/**
 * Стоимость, которую нечем считать: расход окна 0 — или расхода нет вовсе,
 * а стоимость пришла нулём («0 PLN за лид» без расхода — не цена, а пусто).
 */
export const marketingCostWithoutSpend = (
  metrics: IMarketingMetrics | null | undefined,
  key: MarketingMetricKey,
): boolean => {
  if (!metrics || !MARKETING_SPEND_COST_KEYS.includes(key)) return false;
  const spend = spendForCost(metrics);
  if (spend === 0) return true;
  return (
    spend === null && marketingNumber(metrics[key] as MarketingNumeric) === 0
  );
};

/**
 * Показатели из суммы подписанного: сама сумма («Подписано, PLN») и ROAS
 * по подписанному. «Поступило» (и ROAS по нему) — нет: ноль поступлений
 * бывает настоящим.
 */
export const MARKETING_PRICED_KEYS: ReadonlyArray<MarketingMetricKey> = [
  'revenuePln',
  'roas',
];

/**
 * Сумма неизвестна, а не ноль (решение владельца 10.10): продажи в окне
 * есть, а «Подписано, PLN» — 0 — значит, у продаж нет цены (так бывает у
 * договоров без суммы), и «было 0» / «0 PLN» / ROAS 0× врали бы. Тогда у
 * суммы и ROAS по подписанному — «—» с подсказкой, без дельты.
 */
export const marketingRevenueUnknown = (
  metrics: IMarketingMetrics | null | undefined,
  key?: MarketingMetricKey,
): boolean => {
  if (!metrics) return false;
  if (key && !MARKETING_PRICED_KEYS.includes(key)) return false;
  const sales = marketingNumber(metrics.sales) ?? 0;
  return sales > 0 && marketingNumber(metrics.revenuePln) === 0;
};

/** То же по двум числам строки (устройства, воронка SEO). */
export const marketingRevenueUnknownOf = (
  sales: MarketingNumeric | null | undefined,
  revenue: MarketingNumeric | null | undefined,
): boolean =>
  (marketingNumber(sales) ?? 0) > 0 && marketingNumber(revenue) === 0;

/** Ни расхода для стоимости, ни цены для суммы: показать нечего. */
const marketingMetricUnknown = (
  metrics: IMarketingMetrics,
  key: MarketingMetricKey,
): boolean =>
  marketingCostWithoutSpend(metrics, key) ||
  marketingRevenueUnknown(metrics, key);

export const marketingMetricValue = (
  metrics: IMarketingMetrics | null | undefined,
  key: MarketingMetricKey,
): number | null =>
  metrics && !marketingMetricUnknown(metrics, key)
    ? marketingNumber(metrics[key] as MarketingNumeric)
    : null;

export const formatMarketingMetric = (
  metrics: IMarketingMetrics | null | undefined,
  key: MarketingMetricKey,
  lang?: string | null,
): string =>
  formatMarketingValue(
    MARKETING_METRICS[key].format,
    metrics && !marketingMetricUnknown(metrics, key)
      ? (metrics[key] as MarketingNumeric)
      : null,
    lang,
  );

/**
 * Расход для показа. Окно, которое кончается сегодня, почти всегда без
 * одного-двух последних дней (площадки их ещё не отдали), и `spendPln` у
 * него null — честно, но экран из прочерков выглядит сломанным. Тогда
 * показывается расход за собранные дни (`spendCoveredPln`) — приглушённо и
 * с подписью «за собранные дни по …».
 *
 * Только для показа: CPL, стоимость продажи, ROAS и дельта к прошлому окну
 * из частичного расхода не считаются никогда (они читают `spendPln`).
 */
export interface IMarketingSpendView {
  /** Полный расход, иначе расход за собранные дни; `null` — не знаем. */
  value: number | null;
  /** Показан расход только за собранные дни. */
  partial: boolean;
  /** Последний собранный день — для подписи частичного расхода. */
  through: MarketingDate | null;
  /** Стоимость без расхода в окне: «—» с подсказкой, а не 0. */
  noSpend?: boolean;
  /** Сумма подписанного неизвестна (у продаж нет цены): «—», а не 0. */
  noPrice?: boolean;
}

export const marketingSpendView = (
  metrics:
    | Pick<
        IMarketingMetrics,
        'spendPln' | 'spendCoveredPln' | 'spendCoveredThrough'
      >
    | null
    | undefined,
): IMarketingSpendView => {
  const full = marketingNumber(metrics?.spendPln);
  if (full !== null) return { value: full, partial: false, through: null };
  const covered = marketingNumber(metrics?.spendCoveredPln);
  if (covered === null) return { value: null, partial: false, through: null };
  return {
    value: covered,
    partial: true,
    through: metrics?.spendCoveredThrough ?? null,
  };
};

/* ── Отношения за собранные дни ──────────────────────────────────────── */

/** Отношения, у которых есть вариант «за собранные дни». */
export const MARKETING_RATIO_KEYS = [
  'cplPln',
  'costPerSalePln',
  'roas',
  'roasCash',
] as const;

export type MarketingRatioKey = typeof MARKETING_RATIO_KEYS[number];

/** Поле «за собранные дни» каждого отношения. */
const RATIO_COVERED: Readonly<
  Record<MarketingRatioKey, keyof IMarketingMetrics>
> = {
  cplPln: 'cplCoveredPln',
  costPerSalePln: 'costPerSaleCoveredPln',
  roas: 'roasCovered',
  roasCash: 'roasCashCovered',
};

export const isMarketingRatioKey = (key: string): key is MarketingRatioKey =>
  (MARKETING_RATIO_KEYS as readonly string[]).includes(key);

/**
 * Отношение для показа — та же форма, что у расхода: значение, «частичное
 * ли» и последний собранный день для подписи «по ДД.ММ».
 */
export type IMarketingRatioView = IMarketingSpendView;

/**
 * CPL, стоимость продажи, ROAS для показа (решение владельца 29.09).
 *
 * Основное отношение есть — оно. Основного нет (расход окна неполный), а
 * сервер посчитал его за собранные дни (`*Covered`: числитель и знаменатель
 * — за одни и те же дни, последний — `ratiosThrough`), — показывается оно,
 * приглушённо и с подписью «по ДД.ММ», а не прочерк. Нет ни того, ни
 * другого — `null` (прочерк).
 *
 * Только для показа и сортировки таблиц экрана: дельты к прошлому окну и
 * план-факт читают основное отношение.
 */
export const marketingRatioView = (
  metrics: IMarketingMetrics | null | undefined,
  key: MarketingRatioKey,
): IMarketingRatioView => {
  if (marketingCostWithoutSpend(metrics, key)) {
    return { value: null, partial: false, through: null, noSpend: true };
  }
  if (marketingRevenueUnknown(metrics, key)) {
    return { value: null, partial: false, through: null, noPrice: true };
  }
  const full = marketingNumber(metrics?.[key] as MarketingNumeric);
  if (full !== null) return { value: full, partial: false, through: null };
  const covered = marketingNumber(
    metrics?.[RATIO_COVERED[key]] as MarketingNumeric,
  );
  if (covered === null) return { value: null, partial: false, through: null };
  return {
    value: covered,
    partial: true,
    through: metrics?.ratiosThrough ?? null,
  };
};

/**
 * Вид любого показателя для показа: расход — полный или за собранные дни,
 * отношение — основное или за собранные дни, остальное — как пришло (не
 * частичное).
 */
export const marketingMetricView = (
  metrics: IMarketingMetrics | null | undefined,
  key: MarketingMetricKey,
): IMarketingSpendView => {
  if (key === 'spendPln') return marketingSpendView(metrics);
  if (isMarketingRatioKey(key)) return marketingRatioView(metrics, key);
  return {
    value: marketingMetricValue(metrics, key),
    partial: false,
    through: null,
    ...(marketingCostWithoutSpend(metrics, key) ? { noSpend: true } : {}),
    ...(marketingRevenueUnknown(metrics, key) ? { noPrice: true } : {}),
  };
};

/**
 * Показанное значение показателя — для сортировки на экране: иначе столбец
 * из «частичных» не сортировался бы вовсе.
 */
export const marketingShownValue = (
  metrics: IMarketingMetrics | null | undefined,
  key: MarketingMetricKey,
): number | null => marketingMetricView(metrics, key).value;

/**
 * Первый частичный вид среди показанных ячеек — для одной сноски под
 * таблицей («серым — за собранные дни по …»); `null` — частичных нет.
 */
export const marketingFirstPartial = (
  metricsList: ReadonlyArray<IMarketingMetrics | null | undefined>,
  keys: ReadonlyArray<MarketingMetricKey>,
): IMarketingSpendView | null =>
  metricsList
    .flatMap((metrics) => keys.map((key) => marketingMetricView(metrics, key)))
    .find((view) => view.partial) ?? null;

export type MarketingDeltaTone = 'good' | 'bad' | 'neutral';

export interface IMarketingDelta {
  /** Текущее минус прошлое. */
  diff: number;
  /** Изменение в процентах, одна цифра после запятой; `null` — было 0. */
  pct: number | null;
  tone: MarketingDeltaTone;
  /**
   * Повторное ревью 0103 (A12): у долей (конверсия, CTR) — разница в
   * процентных пунктах (11,4 % → 33 % — «+21,6 п. п.», а не «+187,9 %»);
   * у счётчиков и денег поля нет.
   */
  points?: number;
}

/**
 * Знаков после запятой у уже округлённого числа (не больше `max`): «+50»,
 * «+12,5», «−0,57» — без хвостов «,0».
 */
export const marketingFractionDigits = (value: number, max = 2): number => {
  for (let digits = 0; digits < max; digits += 1) {
    const scaled = value * 10 ** digits;
    if (Math.abs(Math.round(scaled) - scaled) < 1e-9) return digits;
  }
  return max;
};

/** Формат доли: разница — в п. п., а не относительный процент. */
export const marketingIsShareFormat = (
  format: MarketingValueFormat | null | undefined,
): boolean => format === 'pct' || format === 'rate';

/**
 * Разница долей (0..1) в процентных пунктах: одна цифра после запятой у
 * долей, две — у малых (CTR, `rate`): «−0,57 п. п.».
 */
export const marketingDeltaPoints = (
  current: number,
  previous: number,
  format: MarketingValueFormat = 'pct',
): number => {
  const scale = format === 'rate' ? 100 : 10;
  return Math.round((current - previous) * 100 * scale) / scale;
};

/**
 * Дельта к прошлому периоду. Любая сторона `null` — дельты нет: «CPL не
 * посчитан → CPL вырос на 100 %» было бы враньём. У долей (`format` —
 * `pct` / `rate`) — ещё и `points`. Изменение, которое после округления
 * показа — ноль («0 %», «0 п. п.»), — нейтральное: «−0 %» красным врало
 * бы о падении (повторное ревью 0103, A11).
 */
export const marketingDelta = (
  current: number | null,
  previous: number | null,
  direction: MarketingMetricDirection,
  format?: MarketingValueFormat | null,
): IMarketingDelta | null => {
  if (current === null || previous === null) return null;
  const diff = current - previous;
  let pct: number | null;
  if (previous === 0) pct = diff === 0 ? 0 : null;
  else pct = Math.round(((diff * 100) / Math.abs(previous)) * 10) / 10;
  const share = marketingIsShareFormat(format);
  const points = share
    ? marketingDeltaPoints(current, previous, format as MarketingValueFormat)
    : undefined;
  const shownZero = share ? points === 0 : pct === 0;
  let tone: MarketingDeltaTone = 'neutral';
  if (diff !== 0 && !shownZero && direction !== 'neutral') {
    tone = diff > 0 === (direction === 'up') ? 'good' : 'bad';
  }
  return share ? { diff, pct, tone, points } : { diff, pct, tone };
};

/**
 * Дельта после округления показа — ноль: «без изменений», без знака,
 * стрелки и цвета (A11).
 */
export const marketingDeltaIsZero = (delta: IMarketingDelta): boolean =>
  delta.diff === 0 ||
  (delta.points !== undefined ? delta.points === 0 : delta.pct === 0);

/** Дельта показателя между двумя наборами метрик. */
export const marketingMetricDelta = (
  current: IMarketingMetrics | null | undefined,
  previous: IMarketingMetrics | null | undefined,
  key: MarketingMetricKey,
): IMarketingDelta | null =>
  marketingDelta(
    marketingMetricValue(current, key),
    marketingMetricValue(previous, key),
    MARKETING_METRICS[key].direction,
    MARKETING_METRICS[key].format,
  );

export interface IMarketingDeltaLabel {
  /**
   * `marketing.delta.pct` | `marketing.delta.points` |
   * `marketing.delta.fromZero` | `marketing.delta.same`
   */
  key: string;
  /** Для `pct` и `points`: «+12,5» / «−3». */
  pct?: string;
}

/** Подпись дельты ключом перевода — плитка переводит сама. */
export const marketingDeltaLabel = (
  delta: IMarketingDelta,
  lang?: string | null,
): IMarketingDeltaLabel => {
  if (marketingDeltaIsZero(delta)) return { key: 'marketing.delta.same' };
  if (delta.points !== undefined) {
    return {
      key: 'marketing.delta.points',
      pct: formatMarketingSigned(
        delta.points,
        lang,
        marketingFractionDigits(delta.points),
      ),
    };
  }
  if (delta.pct === null) return { key: 'marketing.delta.fromZero' };
  const body = formatter('deltaPct', lang, { maximumFractionDigits: 1 }).format(
    Math.abs(delta.pct),
  );
  return {
    key: 'marketing.delta.pct',
    pct: `${delta.pct > 0 ? '+' : '−'}${body}`,
  };
};

/**
 * Конверсия лид → продажа долей. Лидов нет или неизвестно — `null`: «0 %»
 * при нуле лидов выглядело бы провалом, а это просто нечего делить.
 */
export const marketingConversion = (
  sales: MarketingNumeric,
  leads: MarketingNumeric,
): number | null => {
  const saleCount = marketingNumber(sales);
  const leadCount = marketingNumber(leads);
  if (saleCount === null || leadCount === null || leadCount <= 0) return null;
  return saleCount / leadCount;
};

/**
 * `salesCoverage` строки — доля 0..1 (контракт). Мусор и `null` — `null`:
 * экран покажет прочерк, а не «0 %».
 */
export const marketingCoverageShare = (
  value: number | null | undefined,
): number | null => {
  const number = marketingNumber(value);
  if (number === null) return null;
  return Math.min(Math.max(number, 0), 1);
};

/* ── Доли каналов ────────────────────────────────────────────────────── */

export interface IMarketingShareRow {
  channel: MarketingChannelCode;
  spend: number | null;
  /** Расход для показа: полный или за собранные дни (с подписью). */
  spendView: IMarketingSpendView;
  spendShare: number | null;
  leads: number;
  leadsShare: number | null;
  sales: number;
  salesShare: number | null;
  /** Подписано каналом за окно. */
  revenue: number | null;
}

const share = (part: number | null, total: number): number | null =>
  part === null || total <= 0 ? null : part / total;

/**
 * Доли каналов в расходе, CRM-лидах и продажах. Доля расхода — только у
 * канала с известным расходом; сумма долей продаж под `participation` может
 * быть больше 100 % (так устроена модель). Сортировка — по лидам, дальше
 * порядок словаря.
 */
export const marketingChannelShares = (
  channels: ReadonlyArray<IMarketingChannelShare>,
): IMarketingShareRow[] => {
  const spends = channels.map((item) => marketingNumber(item.metrics.spendPln));
  const spendTotal = spends.reduce<number>(
    (sum, value) => sum + (value ?? 0),
    0,
  );
  const leadsTotal = channels.reduce(
    (sum, item) => sum + item.metrics.crmLeads,
    0,
  );
  const salesTotal = channels.reduce(
    (sum, item) => sum + item.metrics.sales,
    0,
  );
  const order = (code: string) => {
    const index = (MARKETING_CHANNELS as string[]).indexOf(code);
    return index < 0 ? MARKETING_CHANNELS.length : index;
  };
  return channels
    .map((item, index) => ({
      channel: item.channel,
      spend: spends[index],
      spendView: marketingSpendView(item.metrics),
      spendShare: share(spends[index], spendTotal),
      leads: item.metrics.crmLeads,
      leadsShare: share(item.metrics.crmLeads, leadsTotal),
      sales: item.metrics.sales,
      salesShare: share(item.metrics.sales, salesTotal),
      revenue: marketingNumber(item.metrics.revenuePln),
    }))
    .sort((a, b) => b.leads - a.leads || order(a.channel) - order(b.channel));
};

/* ── Источники ───────────────────────────────────────────────────────── */

export const MARKETING_SOURCES = [
  'meta',
  'google_ads',
  'kommo',
  'sales',
  'gsc',
  'ga4',
  'bing',
  // Этап 3: поисковые запросы Google Ads — отдельный сбор.
  'search_terms',
  'sendgrid',
  // ТЗ 0103: свой счётчик посещений сайта и SEO-данные DataForSEO.
  'site_tracking',
  'dataforseo',
] as const;

/**
 * ТЗ 0103: источники, которые сервер может ещё не отдавать (DataForSEO —
 * до первого прогона на этом сервере). «Данные» показывают такой строкой
 * «ещё не подключён», а не молчат; пришёл с сервера — обычная строка.
 */
export const MARKETING_PENDING_SOURCES: ReadonlyArray<string> = ['dataforseo'];

/**
 * Источники, которые можно обновить из ERP («Обновить сейчас»): сборщики
 * рекламы, пересборка продаж, (этап 3) лёгкий сбор поиска и сайта и (этап 4)
 * письма SendGrid. Kommo
 * синкается сам — кнопки у него нет, иначе клик получил бы отказ сервера
 * `bad_source`.
 */
export const MARKETING_REFRESHABLE_SOURCES = [
  'meta',
  'google_ads',
  'sales',
  'gsc',
  'ga4',
  'bing',
  'search_terms',
  // Этап 4: письма SendGrid — рассылки, статистика по дням и категориям.
  'sendgrid',
];

export const marketingCanRefreshSource = (source: string): boolean =>
  MARKETING_REFRESHABLE_SOURCES.includes(source);

export type MarketingChipState =
  | 'ok'
  | 'warn'
  | 'error'
  | 'neutral'
  | 'running';

export const MARKETING_CHIP_STATES: MarketingChipState[] = [
  'ok',
  'warn',
  'error',
  'neutral',
  'running',
];

/**
 * Сколько часов тишины источника ещё норма: лёгкий сбор идёт каждые два
 * часа днём, ночной — раз в сутки; дольше суток с запасом — уже отставание.
 */
export const MARKETING_SOURCE_STALE_HOURS = 26;

/**
 * ТЗ 0103: у источников с другим ритмом своя норма тишины (как
 * `STALE_SOURCES` сервера): DataForSEO собирает раз в неделю — 8 суток,
 * свой счётчик посещений сбрасывает каждые минуты — 12 часов.
 */
export const MARKETING_SOURCE_STALE_HOURS_BY: Readonly<
  Record<string, number>
> = {
  dataforseo: 192,
  site_tracking: 12,
};

export const marketingSourceLabelKey = (
  source: string | null | undefined,
): string | null =>
  source && (MARKETING_SOURCES as readonly string[]).includes(source)
    ? `marketing.sources.${source}`
    : null;

/**
 * Состояние чипа свежести. Порядок проверок несущий: идущий сбор важнее
 * старой ошибки, ошибка важнее «давно», а тестовые данные — всегда
 * предупреждение, чтобы их не приняли за настоящие.
 */
export const marketingSourceChipState = (
  status: IMarketingSourceStatus,
  now: Date = new Date(),
): MarketingChipState => {
  if (status.mode === 'DISABLED') return 'neutral';
  if (status.running) return 'running';
  if (status.lastError) return 'error';
  if (status.mode === 'FAKE') return 'warn';
  if (!status.lastSuccessAt) return 'warn';
  const last = DateTime.fromISO(status.lastSuccessAt);
  if (!last.isValid) return 'warn';
  const { hours } = DateTime.fromJSDate(now).diff(last, 'hours');
  const limit =
    MARKETING_SOURCE_STALE_HOURS_BY[status.source] ??
    MARKETING_SOURCE_STALE_HOURS;
  return hours > limit ? 'warn' : 'ok';
};

/**
 * Устаревших источников (включённых, но не «ok»): то же правило, что у
 * замечания «Источники» «Лидов и продаж» и тона вывода (F-E2).
 */
export const marketingStaleSourceCount = (
  sources: ReadonlyArray<IMarketingSourceStatus>,
  now: Date = new Date(),
): number =>
  sources.filter(
    (source) =>
      source.mode !== 'DISABLED' &&
      marketingSourceChipState(source, now) !== 'ok',
  ).length;

/* ── Справочники (подписи — ключами `marketing.dict.<словарь>.<код>`) ─── */

export const MARKETING_DICTS = {
  /** Оффер (`OfferRule.offer`). */
  offers: [
    'direct',
    'dofin',
    'webinar',
    'quiz',
    'ebook',
    'lead_magnet',
    'remarketing',
    'brand',
  ],
  /** Тип воронки Kommo (`KommoPipeline.funnel_kind`). */
  funnelKinds: [
    'sales',
    'webinar',
    'lead_magnet',
    'quiz',
    'ebook',
    'dofin',
    'other',
  ],
  /** Поле правила канала. */
  ruleFields: [
    'utm_source',
    'utm_medium',
    'source_medium',
    'referrer_host',
    'form_name',
    'ga4_source',
    'tracking_key',
  ],
  /** Способ сравнения правила канала. */
  ruleMatches: ['exact', 'iexact', 'prefix', 'contains', 'regex'],
  /** Вид алиаса семейства курса. */
  aliasKinds: [
    'erp_course',
    'pandadoc_bucket',
    'kommo_value',
    'campaign_regex',
    'site_slug',
    'search_regex',
    'group_code',
    'form_slug',
  ],
  /** Поле правила оффера. */
  offerRuleFields: [
    'campaign_name',
    'utm_campaign',
    'form_name',
    'pipeline_kind',
  ],
  /**
   * Поле несопоставленной метки, которого нет в словарях правил (этап 2:
   * кампания без `utm_campaign` узнаётся по `utm_id`).
   */
  unmappedFields: ['utm_id'],
  /** Вид документа продажи. */
  saleKinds: ['direct', 'funding', 'company', 'trainer', 'other'],
  /** Почему документ продажа или нет. */
  saleReasons: [
    'new_course',
    'repeat_same_course',
    'not_direct',
    'course_unknown',
    'repeat_unknown_course',
    'excluded',
  ],
  /** Откуда документ. */
  saleSources: ['esign', 'pandadoc'],
  /** Как касание связано с продажей. */
  matchRules: ['crm_lead_id', 'email', 'phone', 'phone_tail'],
  /** Формат объявления. */
  adFormats: [
    'video',
    'image',
    'carousel',
    'collection',
    'dynamic',
    'text',
    'pmax',
    'unknown',
  ],
  /** Уровень покрытия продажи. */
  coverage: [...MARKETING_COVERAGE_LEVELS],
  /**
   * Статус рекламной сущности — как его отдаёт площадка (Meta
   * `effective_status`, Google Ads `status`). Незнакомый — как пришёл.
   */
  adStatuses: [
    'ACTIVE',
    'ENABLED',
    'PAUSED',
    'CAMPAIGN_PAUSED',
    'ADSET_PAUSED',
    'ARCHIVED',
    'DELETED',
    'REMOVED',
    'IN_PROCESS',
    'PENDING_REVIEW',
    'WITH_ISSUES',
    'DISAPPROVED',
  ],
} as const;

export type MarketingDictName = keyof typeof MARKETING_DICTS;

/** Ключ подписи кода словаря; `null` — кода нет, экран покажет его как есть. */
export const marketingDictLabelKey = (
  dict: MarketingDictName,
  code: string | null | undefined,
): string | null =>
  code && (MARKETING_DICTS[dict] as readonly string[]).includes(code)
    ? `marketing.dict.${dict}.${code}`
    : null;

export type MarketingStatusTone = 'ok' | 'warn' | 'neutral';

/** Тон статуса рекламы: идёт — зелёный, проблема — жёлтый, прочее — серый. */
export const marketingAdStatusTone = (
  status: string | null | undefined,
): MarketingStatusTone => {
  const code = (status ?? '').toUpperCase();
  if (code === 'ACTIVE' || code === 'ENABLED') return 'ok';
  if (code === 'WITH_ISSUES' || code === 'DISAPPROVED') return 'warn';
  return 'neutral';
};

/** Правило «слабой» склейки: только хвост телефона — помечаем. */
export const marketingWeakMatch = (matchRule: string): boolean =>
  matchRule === 'phone_tail';

/* ── CSV таблицы разреза ─────────────────────────────────────────────── */

export interface IMarketingBreakdownCsvHeaders {
  label: string;
  columns: Readonly<Record<MarketingMetricKey, string>>;
  coverage: string;
  /** Подпись итоговой строки. */
  total: string;
}

const csvNumber = (value: MarketingNumeric): string => {
  const number = marketingNumber(value);
  return number === null ? '' : String(number);
};

/**
 * CSV разреза: строки как на экране плюс итог. Пустая ячейка — «не знаем»
 * (как прочерк на экране), а не ноль.
 */
export const buildMarketingBreakdownCsv = (
  rows: ReadonlyArray<IMarketingBreakdownRow>,
  total: IMarketingMetrics | null | undefined,
  headers: IMarketingBreakdownCsvHeaders,
): string => {
  const head = [
    headers.label,
    ...MARKETING_BREAKDOWN_COLUMNS.map((key) => headers.columns[key]),
    headers.coverage,
  ];
  const line = (
    label: string,
    metrics: IMarketingMetrics,
    coverage: number | null | undefined,
  ) => [
    label,
    ...MARKETING_BREAKDOWN_COLUMNS.map((key) =>
      csvNumber(metrics[key] as MarketingNumeric),
    ),
    csvNumber(coverage),
  ];
  const body = rows.map((row) =>
    line(row.label, row.metrics, row.salesCoverage),
  );
  return toCsv(
    total ? [head, ...body, line(headers.total, total, null)] : [head, ...body],
  );
};

/* ── Черновики правил из «Качества данных» ───────────────────────────── */

export const MARKETING_SOURCE_MODES: MarketingSourceModeCode[] = [
  'LIVE',
  'FAKE',
  'DISABLED',
];

/** Экранирование для регулярного выражения: имя кампании — буквально. */
export const marketingEscapeRegex = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Правило канала для несопоставленного значения: поле — то, где значение
 * нашлось (если сервер назвал поле из словаря), сравнение без регистра,
 * канал выбирает человек (по умолчанию «неизвестный» — пусть решит явно).
 */
export const marketingChannelRuleDraft = (
  value: IMarketingUnmappedValue,
): IMarketingChannelRuleInput => ({
  id: null,
  priority: 100,
  field: (MARKETING_DICTS.ruleFields as readonly string[]).includes(value.field)
    ? value.field
    : 'utm_source',
  match: 'iexact',
  pattern: value.value,
  channel: 'UNKNOWN',
  subChannel: '',
  isPaid: false,
  active: true,
  note: '',
});

/** Черновик алиаса семейства для несопоставленной кампании (семейство — выбрать). */
export interface IMarketingAliasDraft {
  kind: string;
  value: string;
}

export const marketingCampaignAliasDraft = (
  value: IMarketingUnmappedValue,
): IMarketingAliasDraft => ({
  kind: 'campaign_regex',
  value: `^${marketingEscapeRegex(value.value)}$`,
});

/* ══ Этап 2 ═════════════════════════════════════════════════════════════ */

/* ── Текст с подстановками ───────────────────────────────────────────── */

/**
 * Готовая к переводу строка: ключ `marketing.*`, подстановки уже в формате
 * языка (числа, проценты, деньги) и `labels` — подстановки, которые сами —
 * ключи перевода (имя площадки, уровень). Модель переводчика не знает:
 * `t(key, { ...params, ...labels переведённые })` делает экран.
 */
export interface IMarketingText {
  key: string;
  params: Record<string, string>;
  labels?: Record<string, string>;
}

interface IMarketingTextVariant {
  /** Хвост ключа: `full` / `short` / `bare`. */
  name: string;
  /** Подстановки варианта; `null` — числа нет, вариант не годится. */
  params: Record<string, string | null>;
  labels?: Record<string, string>;
}

/**
 * Первый вариант, у которого есть ВСЕ подстановки: «с числами», если числа
 * пришли, иначе — короче. Без цифры строка честнее, чем с прочерком внутри.
 */
const pickText = (
  prefix: string,
  variants: IMarketingTextVariant[],
): IMarketingText | null => {
  const found = variants.find((variant) =>
    Object.values(variant.params).every((value) => value !== null),
  );
  if (!found) return null;
  return {
    key: `${prefix}.${found.name}`,
    params: found.params as Record<string, string>,
    ...(found.labels ? { labels: found.labels } : {}),
  };
};

/**
 * Разобранный JSONString сервера (`metrics`, `params` сигнала): строка JSON
 * или уже объект. Мусор — пустой объект: сигнал без доказательств рисуется
 * заголовком, а не роняет ленту.
 */
export const marketingParseJson = (raw: unknown): MarketingJson => {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as MarketingJson)
    : {};
};

/** Число поля JSON; не число — `null` (пропуск ≠ ноль). */
export const marketingJsonNumber = (
  json: MarketingJson | null | undefined,
  key: string,
): number | null => {
  const value = json?.[key];
  return typeof value === 'number' || typeof value === 'string'
    ? marketingNumber(value)
    : null;
};

const jsonText = (json: MarketingJson | null | undefined, key: string) => {
  const value = json?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

/** Процент со знаком: `8.6` (уже в процентах) → «+8,6 %». */
const signedPct = (pct: number | null, lang?: string | null): string | null => {
  if (pct === null) return null;
  const body = formatMarketingPct(Math.abs(pct) / 100, lang);
  if (pct === 0) return body;
  return `${pct > 0 ? '+' : '−'}${body}`;
};

const pctOrNull = (value: number | null, lang?: string | null) =>
  value === null ? null : formatMarketingPct(value, lang);

/** CTR, hook / hold rate — до двух знаков (`formatMarketingRate`). */
const rateOrNull = (value: number | null, lang?: string | null) =>
  value === null ? null : formatMarketingRate(value, lang);

const moneyOrNull = (
  value: number | null,
  lang?: string | null,
  precise = false,
) => (value === null ? null : formatMarketingMoney(value, lang, precise));

const countOrNull = (value: number | null, lang?: string | null) =>
  value === null ? null : formatMarketingCount(value, lang);

const usdOrNull = (value: number | null, lang?: string | null) =>
  value === null ? null : formatMarketingUsd(value, lang);

const ratioOrNull = (value: number | null, lang?: string | null) =>
  value === null ? null : formatMarketingRatio(value, lang);

/** Частота — «2,13»: до двух знаков, не целым счётчиком. */
const frequencyOrNull = (value: number | null, lang?: string | null) =>
  value === null
    ? null
    : formatter('frequency', lang, { maximumFractionDigits: 2 }).format(value);

/** Частота показа объявления человеку («2,13») или «—». */
export const formatMarketingFrequency = (
  value: MarketingNumeric,
  lang?: string | null,
): string => frequencyOrNull(marketingNumber(value), lang) ?? MARKETING_DASH;

/* ── Креативы ────────────────────────────────────────────────────────── */

/**
 * Форматы в фильтре «Креативов» (ТЗ этапа 2). Коллекция и «неизвестно»
 * встречаются чипом в строке, но фильтром не выбираются.
 */
export const MARKETING_CREATIVE_FORMATS = [
  'video',
  'image',
  'carousel',
  'dynamic',
  'text',
  'pmax',
] as const;

export const isMarketingCreativeFormat = (
  value: string | null | undefined,
): boolean =>
  Boolean(value) &&
  (MARKETING_CREATIVE_FORMATS as readonly string[]).includes(value as string);

/** Hook / hold rate есть смысл показывать только у видео. */
export const marketingIsVideo = (format: string | null | undefined): boolean =>
  format === 'video';

/**
 * Столбцы, по которым сервер сортирует креативы (`orderBy`): показатели
 * `MarketingMetrics` и поля строки. `fatigue` — сила выгорания.
 */
export type MarketingCreativeSortKey =
  | 'name'
  | 'ageDays'
  | 'spendPln'
  | 'impressions'
  | 'ctr'
  | 'cpm'
  | 'frequency'
  | 'reach28d'
  | 'cplPlatformPln'
  | 'crmLeads'
  | 'cplPln'
  | 'contractsSent'
  | 'sales'
  | 'roas'
  | 'hookRate'
  | 'fatigue';

export type IMarketingCreativeSort = IMarketingSortOf<MarketingCreativeSortKey>;

/**
 * Ряд объявления — по дням, пока окно не длиннее (как у сервера), дальше —
 * по неделям.
 */
export const MARKETING_CREATIVE_DAY_SERIES_MAX_DAYS = 92;

/** Шаг ряда CTR / CPM объявления для окна фильтра. */
export const marketingCreativeSeriesStep = (
  range: IMarketingRange,
): MarketingGranularityCode =>
  marketingRangeDays(range) <= MARKETING_CREATIVE_DAY_SERIES_MAX_DAYS
    ? 'DAY'
    : 'WEEK';

/** Уровень строки «Креативов» — группа ассетов Performance Max. */
export const MARKETING_ASSET_GROUP_LEVEL = 'asset_group';

/**
 * Формат строки разреза по объявлениям у группы ассетов PMax (у разреза нет
 * `level` / `crmScope`, как у креативов): её CRM — всей кампании.
 */
export const MARKETING_PMAX_FORMAT = 'pmax';

/** Строка — группа ассетов PMax, а не объявление. */
export const marketingIsAssetGroup = (row: {
  level?: string | null;
}): boolean => row.level === MARKETING_ASSET_GROUP_LEVEL;

/**
 * CRM-лиды, продажи и деньги строки — всей родительской кампании (группа
 * ассетов PMax: касания несут только кампанию). Строки одной кампании с
 * таким охватом не складываются.
 */
export const marketingCrmByCampaign = (row: {
  crmScope?: string | null;
}): boolean => row.crmScope === 'campaign';

/**
 * Столбцы «Креативов». По умолчанию — объявление, расход, показы, CTR, CPM,
 * частота, CRM-лиды, CPL, продажи, ROAS; «Все колонки» добавляет возраст,
 * hook / hold, CPL площадки, конверсии площадки и охват 28 дней — за
 * основными, не перетасовывая их.
 */
export type MarketingCreativeColumn =
  | 'spendPln'
  | 'impressions'
  | 'ctr'
  | 'cpm'
  | 'frequency'
  | 'crmLeads'
  | 'cplPln'
  | 'contractsSent'
  | 'sales'
  | 'roas'
  | 'ageDays'
  | 'hookRate'
  | 'cplPlatformPln'
  | 'conversionsPlatform'
  | 'reach28d';

export const MARKETING_CREATIVE_DEFAULT_COLUMNS: MarketingCreativeColumn[] = [
  'spendPln',
  'impressions',
  'ctr',
  'cpm',
  'frequency',
  'crmLeads',
  'cplPln',
  // ТЗ 0103: отправленные договоры объявления (последнее касание).
  'contractsSent',
  'sales',
  'roas',
];

const MARKETING_CREATIVE_EXTRA_COLUMNS: MarketingCreativeColumn[] = [
  'ageDays',
  'hookRate',
  'cplPlatformPln',
  'conversionsPlatform',
  'reach28d',
];

export const marketingCreativeColumns = (
  all: boolean,
): MarketingCreativeColumn[] =>
  all
    ? [
        ...MARKETING_CREATIVE_DEFAULT_COLUMNS,
        ...MARKETING_CREATIVE_EXTRA_COLUMNS,
      ]
    : MARKETING_CREATIVE_DEFAULT_COLUMNS;

/** CRM-столбцы: у группы ассетов PMax в них — цифры всей кампании. */
export const MARKETING_CREATIVE_CRM_COLUMNS: MarketingCreativeColumn[] = [
  'crmLeads',
  'cplPln',
  'sales',
  'roas',
];

/**
 * Сортирует ли сервер по столбцу (`orderBy`). Конверсии площадки — нет:
 * заголовок без кнопки.
 */
export const marketingCreativeSortable = (
  column: MarketingCreativeColumn,
): column is MarketingCreativeColumn & MarketingCreativeSortKey =>
  column !== 'conversionsPlatform';

/** Причины выгорания — в порядке, в каком их перечисляет подсказка. */
export const MARKETING_FATIGUE_REASONS = [
  'ctr_drop_vs_start',
  'ctr_drop_vs_prev',
  'frequency',
  'cpm_up_ctr_down',
  'cpl_up',
  'hook_drop',
  'hold_drop',
] as const;

export type MarketingFatigueReason = typeof MARKETING_FATIGUE_REASONS[number];

export const isMarketingFatigueReason = (
  value: string,
): value is MarketingFatigueReason =>
  (MARKETING_FATIGUE_REASONS as readonly string[]).includes(value);

/**
 * Подвкладки «Лидов и продаж» (`ltab`, ТЗ 0103): тренд (по умолчанию),
 * «неделя к неделе / месяц к месяцу» и когорты (переехали из «Пути»).
 */
export const MARKETING_LEADS_SUBTABS = ['trend', 'periods', 'cohorts'] as const;

export type MarketingLeadsSubTab = typeof MARKETING_LEADS_SUBTABS[number];

/**
 * Подвкладки «Рекламы» (`ctab`, ТЗ 0103): кампании и объявления таблицей
 * (бывшие «Каналы» + «Креативы»), карта объявлений, матрицы (канал × курс,
 * формат × курс) и поисковые запросы Google Ads (бывшие «Поиск и сайт ›
 * запросы в рекламе»).
 */
export const MARKETING_ADS_SUBTABS = [
  'table',
  'map',
  'matrix',
  'terms',
] as const;

export type MarketingAdsSubTab = typeof MARKETING_ADS_SUBTABS[number];

/**
 * Подвкладки «Качества данных» (`qtab` адреса): сводка (пиксель против
 * Kommo, источники входов, покрытие, свежесть, сверка с кабинетами) по
 * умолчанию, сопоставление (несопоставленные источники и кампании) и
 * продажи с касаниями.
 */
export const MARKETING_QUALITY_SUBTABS = [
  'summary',
  'mapping',
  'sales',
] as const;

export type MarketingQualitySubTab = typeof MARKETING_QUALITY_SUBTABS[number];

/**
 * Подвкладки «Пути клиента» (`jtab` адреса, ТЗ 0103): посетители (свой
 * счётчик — данные после старта сбора), страницы (трафик GA4 и посадочные,
 * бывшие «Поиск и сайт › сайт»), воронки (косвенные, внутри New leads, лаг,
 * скорость реакции) и письма. Когорты переехали в «Лиды и продажи».
 */
export const MARKETING_JOURNEY_SUBTABS = [
  'visitors',
  'pages',
  'funnels',
  'email',
] as const;

export type MarketingJourneySubTab = typeof MARKETING_JOURNEY_SUBTABS[number];

/**
 * Вкладки «Настройки → Маркетинг» (ТЗ 0093; до него — подвкладки `settab`
 * вкладки «Настройки» страницы), по смыслу справочников:
 *  - `channels` — правила каналов, правила офферов, сопоставления кампаний
 *    (по умолчанию: сюда ведёт «Создать правило» у источника);
 *  - `courses` — семейства курсов с алиасами (сюда — алиас кампании);
 *  - `signals` — маршрут «сигналы → задачи» и настройки поиска и сайта;
 *  - `pipelines` — типы воронок Kommo.
 * Список живёт у адресов хаба (`SETTINGS_MARKETING_TABS`): вкладку раздела
 * пишет и читает `?tab=` настроек, а не адрес маркетинга.
 */
export const MARKETING_SETTINGS_SUBTABS = SETTINGS_MARKETING_TABS;

export type MarketingSettingsSubTab = SettingsMarketingTab;

/**
 * Вкладка «Настройки → Маркетинг» для кнопки «Настройки маркетинга» на
 * вкладке страницы: реклама — правила каналов, курсы — семейства, SEO —
 * домены, вопросы AI и запросы (`seo`), «Путь клиента» — настройки поиска
 * и сайта с типами страниц (`signals`).
 * Прочим — раздел по умолчанию.
 */
export const marketingSettingsTabFor = (
  tab: MarketingTabId,
): MarketingSettingsSubTab => {
  switch (tab) {
    case 'courses':
      return 'courses';
    case 'seo':
      return 'seo';
    case 'journey':
      return 'signals';
    case 'ads':
    default:
      return 'channels';
  }
};

/**
 * Подвкладки «Источники и план» (`dtab` адреса) — операционка, которая
 * осталась на странице (ТЗ 0093):
 *  - `sources` — состояние источников данных и «Синхронизировать» (Admin);
 *    по умолчанию: сюда ведёт сигнал «источник устарел»;
 *  - `plan` — план по месяцам (свой запрос);
 *  - `offline` — ТЗ 0091: журнал выгрузки продаж в Google Ads и Meta (свой
 *    запрос, только чтение); сюда ведёт задержанный отзыв.
 */
export const MARKETING_DATA_SUBTABS = [
  'sources',
  'quality',
  'mapping',
  'sales',
  'plan',
  'offline',
] as const;

export type MarketingDataSubTab = typeof MARKETING_DATA_SUBTABS[number];

/**
 * Повторное ревью 0103, пакет 2 (P4): служебных вкладок меньше — «Качество
 * данных» живёт подвкладками «Данных» одним параметром `dtab`:
 * `quality` (сводка), `mapping` (сопоставление), `sales` (продажи и
 * касания). Первой остаётся `sources`: голый `tab=data` вшит в задачи
 * сервера («источник устарел», бюджет SEO). Подвкладка «Качества» по
 * подвкладке «Данных»; `null` — не качество.
 */
export const MARKETING_DATA_QUALITY_SUBTABS: Readonly<
  Partial<Record<MarketingDataSubTab, MarketingQualitySubTab>>
> = {
  quality: 'summary',
  mapping: 'mapping',
  sales: 'sales',
};

export const marketingDataQualitySubTab = (
  subTab: MarketingDataSubTab,
): MarketingQualitySubTab | null =>
  MARKETING_DATA_QUALITY_SUBTABS[subTab] ?? null;

/** Подвкладка «Данных» по старому `qtab` «Качества данных». */
export const marketingDataSubTabOfQuality = (
  qtab: string | null | undefined,
): MarketingDataSubTab => {
  if (qtab === 'mapping' || qtab === 'sales') return qtab;
  return 'quality';
};

/**
 * Варианты текста каждой причины (`marketing.fatigue.<причина>.<вариант>`):
 * `full` — с числами «сейчас / раньше», `short` — только «сейчас», `bare` —
 * без чисел. Полноту переводов держит тест локали.
 */
export const MARKETING_FATIGUE_VARIANTS: Readonly<
  Record<typeof MARKETING_FATIGUE_REASONS[number], string[]>
> = {
  ctr_drop_vs_start: ['full', 'short', 'bare'],
  ctr_drop_vs_prev: ['full', 'short', 'bare'],
  frequency: ['full', 'bare'],
  cpm_up_ctr_down: ['full', 'short', 'bare'],
  cpl_up: ['full', 'short', 'bare'],
  hook_drop: ['full', 'short', 'bare'],
  hold_drop: ['full', 'short', 'bare'],
};

/**
 * Причины выгорания словами — с числами «сейчас против раньше» из явных
 * полей `MarketingFatigue` (`ctrStart`, `ctrPrev`, `cpm7d`, `cpmPrev`,
 * `cpl7d`, `cplMedian`, `hookPrev`, `holdPrev`). `ctrBase` сюда не идёт:
 * это то старт, то прошлая неделя, и «против неделей раньше» с ним могло бы
 * оказаться враньём.
 *
 * Числа нет — фраза короче (вариант `short` / `bare`), а не «против —».
 */
export const marketingFatigueEvidence = (
  fatigue: IMarketingFatigue,
  lang?: string | null,
): IMarketingText[] => {
  const ctrNow = rateOrNull(fatigue.ctr7d ?? null, lang);
  const rateVariants = (
    now: string | null,
    base: string | null,
  ): IMarketingTextVariant[] => [
    { name: 'full', params: { now, base } },
    { name: 'short', params: { now } },
  ];
  return fatigue.reasons.map((reason) => {
    const prefix = `marketing.fatigue.${reason}`;
    let variants: IMarketingTextVariant[];
    switch (reason) {
      case 'ctr_drop_vs_start':
        variants = rateVariants(
          ctrNow,
          rateOrNull(fatigue.ctrStart ?? null, lang),
        );
        break;
      case 'ctr_drop_vs_prev':
        variants = rateVariants(
          ctrNow,
          rateOrNull(fatigue.ctrPrev ?? null, lang),
        );
        break;
      case 'frequency':
        variants = [
          {
            name: 'full',
            params: {
              value: frequencyOrNull(fatigue.frequency28d ?? null, lang),
            },
          },
        ];
        break;
      case 'cpm_up_ctr_down':
        variants = [
          {
            name: 'full',
            params: {
              now: moneyOrNull(fatigue.cpm7d ?? null, lang, true),
              before: moneyOrNull(fatigue.cpmPrev ?? null, lang, true),
            },
          },
          {
            name: 'short',
            params: { pct: signedPct(fatigue.cpmChangePct ?? null, lang) },
          },
        ];
        break;
      case 'cpl_up':
        variants = [
          {
            name: 'full',
            params: {
              now: moneyOrNull(fatigue.cpl7d ?? null, lang),
              base: moneyOrNull(fatigue.cplMedian ?? null, lang),
            },
          },
          {
            name: 'short',
            params: { ratio: ratioOrNull(fatigue.cplRatio ?? null, lang) },
          },
        ];
        break;
      case 'hook_drop':
        variants = rateVariants(
          rateOrNull(fatigue.hookRate ?? null, lang),
          rateOrNull(fatigue.hookPrev ?? null, lang),
        );
        break;
      case 'hold_drop':
        variants = rateVariants(
          rateOrNull(fatigue.holdRate ?? null, lang),
          rateOrNull(fatigue.holdPrev ?? null, lang),
        );
        break;
      default:
        return { key: 'marketing.fatigue.other', params: { code: reason } };
    }
    return (
      pickText(prefix, [...variants, { name: 'bare', params: {} }]) ?? {
        key: `${prefix}.bare`,
        params: {},
      }
    );
  });
};

/**
 * Выгорание из чисел-доказательств сигнала `creative_fatigue` (snake_case
 * `metrics`) — строка ленты «Пульса» говорит теми же словами, что бейдж в
 * «Креативах».
 */
export const marketingFatigueFromSignal = (
  metrics: MarketingJson,
): IMarketingFatigue => {
  const n = (key: string) => marketingJsonNumber(metrics, key);
  return {
    flagged: true,
    reasons: Array.isArray(metrics.reasons)
      ? (metrics.reasons as unknown[]).filter(
          (item): item is string => typeof item === 'string',
        )
      : [],
    ctr7d: n('ctr_7d'),
    ctrBase: n('ctr_base'),
    ctrStart: n('ctr_start'),
    ctrPrev: n('ctr_prev'),
    frequency28d: n('frequency_28d'),
    cpm7d: n('cpm_7d'),
    cpmPrev: n('cpm_prev'),
    cpmChangePct: n('cpm_change_pct'),
    cpl7d: n('cpl_7d'),
    cplMedian: n('cpl_median'),
    cplRatio: n('cpl_ratio'),
    hookRate: n('hook_rate'),
    hookPrev: n('hook_prev'),
    holdRate: n('hold_rate'),
    holdPrev: n('hold_prev'),
  };
};

/* ── Матрица «формат × курс» ─────────────────────────────────────────── */

export interface IMarketingFormatCell {
  ads: number;
  /** Расход для показа: полный или за собранные дни; `value: null` — нет. */
  spend: IMarketingSpendView;
  crmLeads: number;
  sales: number;
  /** CPL для показа: основной или за собранные дни. */
  cpl: IMarketingRatioView;
}

export interface IMarketingFormatMatrix {
  /** Строки — форматы, по убыванию расхода. */
  formats: string[];
  /** Столбцы — семейства (`MARKETING_NONE_KEY` — курс не определён). */
  families: string[];
  cells: Record<string, IMarketingFormatCell>;
}

export const marketingMatrixKey = (format: string, family: string): string =>
  `${format}|${family}`;

/**
 * «Формат × курс» из `formats` сервера («формат × семейство × оффер»).
 *
 * Клетка с одной группой (так почти всегда, и всегда у PMax: CRM групп
 * ассетов — от кампании, и сервер уже посчитал его в группе ОДИН раз) —
 * ровно цифры сервера, без пересчёта: расход и CPL с их «за собранные дни».
 * Офферы одного формата и курса складываются только там, где групп
 * несколько (у разных офферов разные объявления, сумма не двоит). Пропуск ≠
 * ноль: неизвестный расход любой группы делает неизвестной всю клетку; CPL
 * сложенной клетки — только из полного расхода.
 */
export const marketingFormatMatrix = (
  stats: ReadonlyArray<IMarketingFormatStat>,
): IMarketingFormatMatrix => {
  const groups: Record<string, IMarketingFormatStat[]> = {};
  const formatSpend = new Map<string, number>();
  const familySpend = new Map<string, number>();
  stats.forEach((stat) => {
    const family = stat.familySlug || MARKETING_NONE_KEY;
    const key = marketingMatrixKey(stat.format, family);
    groups[key] = [...(groups[key] ?? []), stat];
    const spend = marketingSpendView(stat.metrics).value ?? 0;
    formatSpend.set(stat.format, (formatSpend.get(stat.format) ?? 0) + spend);
    familySpend.set(family, (familySpend.get(family) ?? 0) + spend);
  });
  const cells: Record<string, IMarketingFormatCell> = {};
  Object.entries(groups).forEach(([key, group]) => {
    const [first] = group;
    if (group.length === 1) {
      cells[key] = {
        ads: first.ads,
        spend: marketingSpendView(first.metrics),
        crmLeads: first.metrics.crmLeads,
        sales: first.metrics.sales,
        cpl: marketingRatioView(first.metrics, 'cplPln'),
      };
      return;
    }
    const views = group.map((stat) => marketingSpendView(stat.metrics));
    const known = views.every((view) => view.value !== null);
    const partial = views.some((view) => view.partial);
    const throughs = views
      .map((view) => view.through)
      .filter((day): day is MarketingDate => Boolean(day))
      .sort();
    const spendValue = known
      ? views.reduce((sum, view) => sum + (view.value ?? 0), 0)
      : null;
    const crmLeads = group.reduce(
      (sum, stat) => sum + stat.metrics.crmLeads,
      0,
    );
    cells[key] = {
      ads: group.reduce((sum, stat) => sum + stat.ads, 0),
      spend: {
        value: spendValue,
        partial: known && partial,
        through: known && partial ? throughs[0] ?? null : null,
      },
      crmLeads,
      sales: group.reduce((sum, stat) => sum + stat.metrics.sales, 0),
      cpl: {
        value:
          known && !partial && spendValue !== null && crmLeads > 0
            ? spendValue / crmLeads
            : null,
        partial: false,
        through: null,
      },
    };
  });
  const bySpend = (spend: Map<string, number>) =>
    Array.from(spend.keys()).sort(
      (a, b) =>
        (spend.get(b) ?? 0) - (spend.get(a) ?? 0) ||
        // «Не определено» — в хвосте при равенстве.
        Number(a === MARKETING_NONE_KEY) - Number(b === MARKETING_NONE_KEY) ||
        a.localeCompare(b),
    );
  return {
    formats: bySpend(formatSpend),
    families: bySpend(familySpend),
    cells,
  };
};

/* ── Путь клиента ────────────────────────────────────────────────────── */

/**
 * Меньше стольких человек в корзине — без процентов, только штуки (как у
 * Hermes): доля от 7 человек — шум, который выглядит выводом.
 */
export const MARKETING_JOURNEY_MIN_BASE = 30;

/** Корзины «внутри New leads» — число косвенных касаний до входа. */
export const MARKETING_WITHIN_KEYS = ['0', '1', '2plus'] as const;

/** Лаг лид → продажа, дни. */
export const MARKETING_LAG_KEYS = [
  '0_7',
  '8_14',
  '15_30',
  '31_60',
  '61plus',
  'unknown',
] as const;

/** Скорость первой реакции: вход в New leads → первая смена этапа. */
export const MARKETING_SPEED_KEYS = [
  'lt1h',
  '1_4h',
  '4_24h',
  'gt24h',
  'unknown',
] as const;

/** Косвенные воронки пути клиента (продают только в New leads). */
export const MARKETING_INDIRECT_KINDS = [
  'webinar',
  'quiz',
  'lead_magnet',
  'ebook',
  'dofin',
] as const;

/** Доля части от базы — только при базе не меньше `MIN_BASE`. */
export const marketingBaseShare = (part: number, base: number): number | null =>
  base >= MARKETING_JOURNEY_MIN_BASE && base > 0 ? part / base : null;

/**
 * Корзины в порядке словаря; незнакомые ключи — в хвосте, чтобы новое с
 * сервера было видно, а не пропало.
 */
export const marketingOrderBuckets = <T extends { key: string }>(
  buckets: ReadonlyArray<T>,
  keys: ReadonlyArray<string>,
): T[] => {
  const rank = (key: string) => {
    const index = keys.indexOf(key);
    return index < 0 ? keys.length : index;
  };
  return [...buckets].sort((a, b) => rank(a.key) - rank(b.key));
};

/** Всего людей в корзинах (без «неизвестно», если попросили). */
export const marketingBucketTotal = (
  buckets: ReadonlyArray<IMarketingJourneyBucket>,
  field: 'people' | 'buyers' = 'people',
): number => buckets.reduce((sum, bucket) => sum + bucket[field], 0);

/**
 * Устройство заявки (29.09) — строки блока «Устройство заявки» в этом
 * порядке; `unknown` — звонки, квизы, формы без устройства и продажи без
 * лида.
 */
export const MARKETING_DEVICE_KEYS = [
  'mobile',
  'desktop',
  'tablet',
  'unknown',
] as const;

export type MarketingDeviceKey = typeof MARKETING_DEVICE_KEYS[number];

/** Код устройства с сервера → ключ словаря; незнакомое — `unknown`. */
export const marketingDeviceKey = (
  code: string | null | undefined,
): MarketingDeviceKey => {
  const lower = (code ?? '').toLowerCase();
  return (MARKETING_DEVICE_KEYS as readonly string[]).includes(lower)
    ? (lower as MarketingDeviceKey)
    : 'unknown';
};

export interface IMarketingDeviceTotals {
  crmLeads: number;
  crmLeadsRepeat: number;
  sales: number;
  revenuePln: number;
}

/**
 * Строки блока «Устройство заявки» в порядке словаря и строка «Итого» —
 * сумма строк (сервер отдаёт их так, что сумма = итогу окна).
 */
export const marketingDeviceTable = (
  rows: ReadonlyArray<IMarketingDeviceRow>,
): { rows: IMarketingDeviceRow[]; total: IMarketingDeviceTotals } => {
  const rank = (code: string) =>
    MARKETING_DEVICE_KEYS.indexOf(marketingDeviceKey(code));
  const ordered = [...rows].sort((a, b) => rank(a.device) - rank(b.device));
  const total = ordered.reduce<IMarketingDeviceTotals>(
    (sum, row) => ({
      crmLeads: sum.crmLeads + row.crmLeads,
      crmLeadsRepeat: sum.crmLeadsRepeat + row.crmLeadsRepeat,
      sales: sum.sales + row.sales,
      revenuePln: sum.revenuePln + (marketingNumber(row.revenuePln) ?? 0),
    }),
    { crmLeads: 0, crmLeadsRepeat: 0, sales: 0, revenuePln: 0 },
  );
  return { rows: ordered, total };
};

/* ── План-факт ───────────────────────────────────────────────────────── */

/** Показатели плана (`TargetMetric` бэкенда) — в порядке полосы «Пульса». */
export const MARKETING_TARGET_METRICS = [
  'crm_leads',
  'sales',
  'revenue_pln',
  'spend_pln',
  'cpl_pln',
  'cost_per_sale_pln',
] as const;

const TARGET_METRIC_KEYS: Readonly<Record<string, MarketingMetricKey>> = {
  crm_leads: 'crmLeads',
  sales: 'sales',
  revenue_pln: 'revenuePln',
  spend_pln: 'spendPln',
  cpl_pln: 'cplPln',
  cost_per_sale_pln: 'costPerSalePln',
};

/** Показатель экрана для кода плана (подпись, формат, «куда лучше»). */
export const marketingTargetMetricKey = (
  code: string,
): MarketingMetricKey | null => TARGET_METRIC_KEYS[code] ?? null;

export type MarketingPlanTone = 'ok' | 'warn' | 'danger' | 'neutral';

/** Темп ниже 90 % плана — внимание; ниже — отставание. */
export const MARKETING_PLAN_WARN_SHARE = 0.9;
/** Затраты (CPL, стоимость продажи) до +10 % к плану — внимание. */
export const MARKETING_PLAN_COST_WARN_SHARE = 1.1;
/** Бюджет: до +5 % — в норме, до +20 % — внимание, дальше — перерасход. */
export const MARKETING_PLAN_BUDGET_OK_SHARE = 1.05;
export const MARKETING_PLAN_BUDGET_WARN_SHARE = 1.2;

export interface IMarketingPlanFactView {
  metric: string;
  metricKey: MarketingMetricKey | null;
  target: number | null;
  fact: number | null;
  pace: number | null;
  /** Факт / план, 0..1 — ширина полосы. */
  progress: number | null;
  /** Темп / план (у затрат — факт / план). */
  paceShare: number | null;
  tone: MarketingPlanTone;
}

/**
 * План-факт словами светофора. «Больше — лучше» (лиды, продажи,
 * подписано) судится по ТЕМПУ на конец месяца; затраты (CPL, стоимость
 * продажи) — по факту: у отношений темп и есть факт; расход — как бюджет,
 * плохо его превысить. Неизвестный факт или темп — нейтрально, а не «плохо».
 */
export const marketingPlanFactView = (
  row: IMarketingPlanFact,
): IMarketingPlanFactView => {
  const metricKey = marketingTargetMetricKey(row.metric);
  const target = marketingNumber(row.target);
  const fact = marketingNumber(row.fact);
  const pace = marketingNumber(row.pace);
  const direction = metricKey ? MARKETING_METRICS[metricKey].direction : 'up';
  const ofTarget = (value: number | null) =>
    value === null || target === null || target <= 0 ? null : value / target;
  const paceShare = direction === 'down' ? ofTarget(fact) : ofTarget(pace);
  let tone: MarketingPlanTone = 'neutral';
  if (paceShare !== null) {
    if (direction === 'up') {
      if (paceShare >= 1) tone = 'ok';
      else if (paceShare >= MARKETING_PLAN_WARN_SHARE) tone = 'warn';
      else tone = 'danger';
    } else if (direction === 'down') {
      if (paceShare <= 1) tone = 'ok';
      else if (paceShare <= MARKETING_PLAN_COST_WARN_SHARE) tone = 'warn';
      else tone = 'danger';
    } else if (paceShare <= MARKETING_PLAN_BUDGET_OK_SHARE) tone = 'ok';
    else if (paceShare <= MARKETING_PLAN_BUDGET_WARN_SHARE) tone = 'warn';
    else tone = 'danger';
  }
  const progress = ofTarget(fact);
  return {
    metric: row.metric,
    metricKey,
    target,
    fact,
    pace,
    progress: progress === null ? null : Math.min(Math.max(progress, 0), 1),
    paceShare,
    tone,
  };
};

/** Первое число месяца дня: `2026-09-28` → `2026-09-01`. */
export const marketingMonthStart = (day: MarketingDate): MarketingDate =>
  `${day.slice(0, 7)}-01`;

/** Месяц со сдвигом: `2026-09-01`, −1 → `2026-08-01`. */
export const marketingShiftMonth = (
  month: MarketingDate,
  delta: number,
): MarketingDate =>
  isoOf(calendarDay(marketingMonthStart(month)).plus({ months: delta }));

/** «сентябрь 2026» / «wrzesień 2026». */
export const marketingMonthLabel = (
  month: MarketingDate,
  lang?: string | null,
): string => {
  const value = calendarDay(marketingMonthStart(month));
  if (!value.isValid) return month;
  return value.setLocale(localeOf(lang)).toFormat('LLLL yyyy');
};

/* ── Сигналы ─────────────────────────────────────────────────────────── */

/** Виды сигналов этапа 2 — у каждого свой заголовок и строка доказательств. */
export const MARKETING_SIGNAL_KINDS = [
  'creative_fatigue',
  'spend_no_leads',
  'cpl_spike',
  'tracking_gap',
  'coverage_drop',
  'source_stale',
  'campaign_mapping_gap',
  // Этап 3: спрос и позиции в поиске.
  'demand_unserved',
  'demand_rising',
  'seo_position_drop',
  // Этап 4: гипотезы по бюджету — свой блок «Пульса», не лента.
  'budget_hypothesis',
  // ТЗ 0091: отзыв офлайн-конверсий задержан — кандидатов больше порога.
  'offline_retract_hold',
  // ТЗ 0103 §E6: договоры отправлены, подписей нет; CRM-лиды есть, продаж
  // нет (кампания или объявление).
  'sent_not_signed',
  'leads_no_sales',
  // ТЗ 0103 §C (этап 3): посадочная стала хуже конвертировать в заявку.
  'page_conversion_drop',
  // ТЗ 0103 §E6 (этап 4): SEO — видимость курса упала, конкурент растёт,
  // AI реже называет нас, новые разрывы месяца, прогон остановлен бюджетом.
  'serp_rank_drop',
  'competitor_gain',
  'ai_visibility_drop',
  'seo_gap_new',
  'seo_budget_hold',
] as const;

export type MarketingSignalKind = typeof MARKETING_SIGNAL_KINDS[number];

export const isMarketingSignalKind = (
  value: string | null | undefined,
): value is MarketingSignalKind =>
  Boolean(value) &&
  (MARKETING_SIGNAL_KINDS as readonly string[]).includes(value as string);

/** Лента показывает открытые и принятые; скрытые и ушедшие — нет. */
export const MARKETING_SIGNAL_FEED_STATUSES = ['open', 'ack'];

/** Статусы решения человека (`ackMarketingSignal`). */
export const MARKETING_SIGNAL_DECISIONS = ['ack', 'dismissed'] as const;

export type MarketingSignalTone = 'danger' | 'warn' | 'info';

export const marketingSignalTone = (
  severity: string | null | undefined,
): MarketingSignalTone => {
  if (severity === 'critical') return 'danger';
  if (severity === 'warning') return 'warn';
  return 'info';
};

/**
 * Варианты строки доказательств (`marketing.signals.<вид>.evidence.<вариант>`)
 * и заголовка (`marketing.signals.<вид>.<вариант>`). Полноту держит тест
 * локали.
 */
export const MARKETING_SIGNAL_TEXT_KEYS: Readonly<
  Record<MarketingSignalKind, string[]>
> = {
  creative_fatigue: ['title'],
  spend_no_leads: [
    'title',
    'evidence.full',
    'evidence.noPlatform',
    'evidence.bare',
  ],
  cpl_spike: ['title', 'evidence.full', 'evidence.bare'],
  tracking_gap: ['title', 'evidence.full', 'evidence.bare'],
  coverage_drop: ['title', 'evidence.full', 'evidence.bare'],
  source_stale: ['title', 'titleNever', 'evidence.full', 'evidence.never'],
  campaign_mapping_gap: [
    'title',
    'evidence.full',
    'evidence.noTop',
    'evidence.bare',
  ],
  demand_unserved: [
    'title',
    'evidence.full',
    'evidence.noTop',
    'evidence.new',
    'evidence.newNoTop',
    'evidence.bare',
  ],
  demand_rising: [
    'title',
    'titleNew',
    'evidence.full',
    'evidence.noTop',
    'evidence.new',
    'evidence.newNoTop',
    'evidence.bare',
  ],
  seo_position_drop: [
    'title',
    'titleNoQuery',
    'evidence.full',
    'evidence.bare',
  ],
  budget_hypothesis: [
    'other',
    'base90',
    'scale_up.title',
    'scale_up.evidence.full',
    'scale_up.evidence.bare',
    'cut.title',
    'cut.evidence.expensive',
    'cut.evidence.noSales',
    'cut.evidence.expected',
    'cut.evidence.bare',
    'refresh_creative.title',
    'refresh_creative.evidence.full',
    'refresh_creative.evidence.top',
    'refresh_creative.evidence.bare',
    'fix_tracking.title',
    'fix_tracking.evidence.full',
    'fix_tracking.evidence.bare',
    'fix_tracking.gaps.campaign_mapping_gap',
    'fix_tracking.gaps.campaign_mapping_gapBare',
    'fix_tracking.gaps.tracking_gap',
    'fix_tracking.gaps.tracking_gapBare',
  ],
  offline_retract_hold: ['title', 'evidence.bare'],
  sent_not_signed: [
    'title',
    'evidence.full',
    'evidence.noValue',
    'evidence.bare',
  ],
  leads_no_sales: [
    'title',
    'evidence.full',
    'evidence.noSpend',
    'evidence.bare',
  ],
  page_conversion_drop: [
    'title',
    'titleNoDrop',
    'evidence.full',
    'evidence.noEntrances',
    'evidence.bare',
  ],
  serp_rank_drop: ['title', 'evidence.full', 'evidence.noTop', 'evidence.bare'],
  competitor_gain: [
    'title',
    'evidence.full',
    'evidence.noTop',
    'evidence.bare',
  ],
  ai_visibility_drop: ['title', 'evidence.full', 'evidence.bare'],
  seo_gap_new: ['title', 'titleNone', 'evidence.full', 'evidence.bare'],
  seo_budget_hold: ['title', 'evidence.full', 'evidence.bare'],
};

/** Средняя позиция в поиске — «14,2»; не число — `null`. */
const positionOrNull = (value: number | null, lang?: string | null) =>
  value === null ? null : formatMarketingPosition(value, lang);

/** Подписи уровня рекламной сущности в тексте сигнала. */
export const MARKETING_SIGNAL_LEVELS = ['campaign', 'adset', 'ad'] as const;

const levelLabel = (level: string | null): Record<string, string> =>
  level && (MARKETING_SIGNAL_LEVELS as readonly string[]).includes(level)
    ? { level: `marketing.signals.levels.${level}` }
    : { level: 'marketing.signals.levels.ad' };

/** Имя площадки/источника кодом базы → ключ подписи (или как есть). */
const sourceLabels = (
  code: string | null,
  name = 'platform',
): { params: Record<string, string>; labels: Record<string, string> } => {
  const key = marketingSourceLabelKey(code);
  return key
    ? { params: {}, labels: { [name]: key } }
    : { params: { [name]: code ?? MARKETING_DASH }, labels: {} };
};

/* ── Этап 4: гипотезы по бюджету (`budget_hypothesis`) ──────────────── */

/** Вид сигнала гипотез: у них свой блок «Пульса», в ленту они не идут. */
export const MARKETING_HYPOTHESIS_SIGNAL = 'budget_hypothesis';

/** `params.kind` гипотезы — действие, которое она предлагает. */
export const MARKETING_HYPOTHESIS_KINDS = [
  'scale_up',
  'cut',
  'refresh_creative',
  'fix_tracking',
] as const;

export type MarketingHypothesisKind = typeof MARKETING_HYPOTHESIS_KINDS[number];

export const isMarketingHypothesisKind = (
  value: unknown,
): value is MarketingHypothesisKind =>
  typeof value === 'string' &&
  (MARKETING_HYPOTHESIS_KINDS as readonly string[]).includes(value);

/** Уверенность гипотезы — по размеру базы (`params.confidence`). */
export const MARKETING_CONFIDENCE_LEVELS = ['low', 'medium', 'high'] as const;

export type MarketingConfidence = typeof MARKETING_CONFIDENCE_LEVELS[number];

/**
 * По чему сервер мерил уверенность (`metrics.confidence_basis`): продажи
 * кампании, ожидаемые продажи (расход / медиана), показы выгорающих
 * объявлений, лиды площадки.
 */
export const MARKETING_CONFIDENCE_BASES = [
  'sales',
  'expected_sales',
  'impressions',
  'leads',
] as const;

/** Тон метки уверенности — общая палитра `L.Tag`. */
export const MARKETING_CONFIDENCE_TONES: Readonly<
  Record<MarketingConfidence, 'ok' | 'info' | 'neutral'>
> = { high: 'ok', medium: 'info', low: 'neutral' };

export const marketingIsHypothesis = (signal: IMarketingSignal): boolean =>
  signal.kind === MARKETING_HYPOTHESIS_SIGNAL;

/** Лента «Пульса» — все сигналы, кроме гипотез (у них свой блок). */
export const marketingFeedSignals = (
  signals: ReadonlyArray<IMarketingSignal>,
): IMarketingSignal[] =>
  signals.filter((signal) => !marketingIsHypothesis(signal));

export const marketingHypothesisKind = (
  signal: IMarketingSignal,
): MarketingHypothesisKind | null => {
  const { kind } = signal.params;
  return isMarketingHypothesisKind(kind) ? kind : null;
};

export const marketingHypothesisConfidence = (
  signal: IMarketingSignal,
): MarketingConfidence | null => {
  const value = signal.params.confidence;
  return typeof value === 'string' &&
    (MARKETING_CONFIDENCE_LEVELS as readonly string[]).includes(value)
    ? (value as MarketingConfidence)
    : null;
};

export const marketingHypothesisBasis = (
  signal: IMarketingSignal,
): typeof MARKETING_CONFIDENCE_BASES[number] | null => {
  const value = signal.metrics.confidence_basis;
  return typeof value === 'string' &&
    (MARKETING_CONFIDENCE_BASES as readonly string[]).includes(value)
    ? (value as typeof MARKETING_CONFIDENCE_BASES[number])
    : null;
};

/**
 * Площадка гипотезы кодом базы (`meta`): `params.platform`, иначе канал
 * сигнала (`META` → `meta`).
 */
const hypothesisPlatform = (signal: IMarketingSignal): string | null =>
  jsonText(signal.params, 'platform') ??
  (signal.channel ? signal.channel.toLowerCase() : null);

/**
 * Заголовок гипотезы: действие и предмет — «Масштабировать кампанию «X»»,
 * «Починить разметку Meta». Незнакомое действие — общий заголовок с кодом:
 * новая гипотеза сервера видна, а не пропадает.
 */
export const marketingHypothesisTitle = (
  signal: IMarketingSignal,
): IMarketingText => {
  const prefix = `marketing.signals.${MARKETING_HYPOTHESIS_SIGNAL}`;
  const kind = marketingHypothesisKind(signal);
  const name =
    jsonText(signal.params, 'name') ?? signal.entityName ?? MARKETING_DASH;
  if (kind === 'fix_tracking') {
    const platform = sourceLabels(hypothesisPlatform(signal));
    return {
      key: `${prefix}.fix_tracking.title`,
      params: platform.params,
      labels: platform.labels,
    };
  }
  if (kind) return { key: `${prefix}.${kind}.title`, params: { name } };
  return {
    key: `${prefix}.other`,
    params: { kind: jsonText(signal.params, 'kind') ?? signal.kind },
  };
};

/**
 * Доказательства гипотезы словами, строками: главное число — первой строкой,
 * база (расход и продажи за 90 дней) — второй. Нет числа — короче, а не с
 * прочерком внутри. Пусто — сказать нечего.
 */
export const marketingHypothesisEvidence = (
  signal: IMarketingSignal,
  lang?: string | null,
): IMarketingText[] => {
  const m = signal.metrics;
  const p = signal.params;
  const n = (key: string) => marketingJsonNumber(m, key);
  const prefix = `marketing.signals.${MARKETING_HYPOTHESIS_SIGNAL}`;
  const lines: Array<IMarketingText | null> = [];
  const base90 = () =>
    pickText(prefix, [
      {
        name: 'base90',
        params: {
          spend: moneyOrNull(n('spend_90d'), lang),
          sales:
            n('sales_linear_90d') === null
              ? null
              : formatMarketingSales(n('sales_linear_90d'), lang),
        },
      },
    ]);
  const cps = moneyOrNull(
    marketingJsonNumber(p, 'cost_per_sale') ?? n('cost_per_sale_90d'),
    lang,
  );
  const median = moneyOrNull(
    marketingJsonNumber(p, 'median_cps') ?? n('platform_median_cps'),
    lang,
  );
  switch (marketingHypothesisKind(signal)) {
    case 'scale_up': {
      const pct = marketingJsonNumber(p, 'budget_lost_pct');
      const lost =
        pct === null
          ? pctOrNull(n('budget_lost_is_7d'), lang)
          : pctOrNull(pct / 100, lang);
      lines.push(
        pickText(`${prefix}.scale_up.evidence`, [
          { name: 'full', params: { lost, cps, median } },
          { name: 'bare', params: {} },
        ]),
        base90(),
      );
      break;
    }
    case 'cut': {
      const branch = jsonText(p, 'branch') ?? jsonText(m, 'branch');
      if (branch === 'no_sales') {
        const pct = marketingJsonNumber(p, 'mapped_pct');
        lines.push(
          pickText(`${prefix}.cut.evidence`, [
            {
              name: 'noSales',
              params: {
                spend: moneyOrNull(
                  marketingJsonNumber(p, 'spend') ?? n('spend_90d'),
                  lang,
                ),
                mapped:
                  pct === null
                    ? pctOrNull(n('mapped_share_90d'), lang)
                    : pctOrNull(pct / 100, lang),
              },
            },
            { name: 'bare', params: {} },
          ]),
        );
        const expected = n('expected_sales');
        if (expected !== null) {
          lines.push({
            key: `${prefix}.cut.evidence.expected`,
            params: { expected: formatMarketingSales(expected, lang) },
          });
        }
      } else {
        lines.push(
          pickText(`${prefix}.cut.evidence`, [
            {
              name: 'expensive',
              params: {
                cps,
                median,
                ratio: ratioOrNull(
                  marketingJsonNumber(p, 'ratio') ?? n('ratio'),
                  lang,
                ),
              },
            },
            { name: 'bare', params: {} },
          ]),
          base90(),
        );
      }
      break;
    }
    case 'refresh_creative': {
      lines.push(
        pickText(`${prefix}.refresh_creative.evidence`, [
          {
            name: 'full',
            params: {
              share: pctOrNull(n('fatigued_share'), lang),
              fatigued: moneyOrNull(n('fatigued_spend_7d'), lang),
              total: moneyOrNull(n('ads_spend_7d'), lang),
              ads: countOrNull(
                n('fatigued_ads') ?? marketingJsonNumber(p, 'ads'),
                lang,
              ),
            },
          },
          { name: 'bare', params: {} },
        ]),
      );
      const top = Array.isArray(m.top_fatigued_ads)
        ? marketingParseJson((m.top_fatigued_ads as unknown[])[0])
        : null;
      const topName = top ? jsonText(top, 'name') : null;
      const topSpend = top ? marketingJsonNumber(top, 'spend_7d') : null;
      if (topName && topSpend !== null) {
        lines.push({
          key: `${prefix}.refresh_creative.evidence.top`,
          params: {
            name: topName,
            spend: formatMarketingMoney(topSpend, lang),
          },
        });
      }
      break;
    }
    case 'fix_tracking': {
      const platform = sourceLabels(hypothesisPlatform(signal));
      const spend = moneyOrNull(
        n('spend_7d') ?? marketingJsonNumber(p, 'spend'),
        lang,
      );
      lines.push(
        spend === null
          ? {
              key: `${prefix}.fix_tracking.evidence.bare`,
              params: platform.params,
              labels: platform.labels,
            }
          : {
              key: `${prefix}.fix_tracking.evidence.full`,
              params: { ...platform.params, spend },
              labels: platform.labels,
            },
      );
      const gaps = Array.isArray(m.gaps) ? (m.gaps as unknown[]) : [];
      gaps.forEach((item) => {
        const gap = marketingParseJson(item);
        const kind = jsonText(gap, 'kind');
        if (kind === 'campaign_mapping_gap') {
          lines.push(
            pickText(`${prefix}.fix_tracking.gaps`, [
              {
                name: 'campaign_mapping_gap',
                params: {
                  pct: pctOrNull(
                    marketingJsonNumber(gap, 'mapped_share'),
                    lang,
                  ),
                  leads: countOrNull(
                    marketingJsonNumber(gap, 'crm_leads'),
                    lang,
                  ),
                },
              },
              { name: 'campaign_mapping_gapBare', params: {} },
            ]),
          );
        } else if (kind === 'tracking_gap') {
          lines.push(
            pickText(`${prefix}.fix_tracking.gaps`, [
              {
                name: 'tracking_gap',
                params: {
                  pct: pctOrNull(marketingJsonNumber(gap, 'lost_share'), lang),
                  leads: countOrNull(
                    marketingJsonNumber(gap, 'platform_leads'),
                    lang,
                  ),
                },
              },
              { name: 'tracking_gapBare', params: {} },
            ]),
          );
        }
      });
      break;
    }
    default:
      break;
  }
  return lines.filter((line): line is IMarketingText => line !== null);
};

/** Системы AI сигналов SEO (подпись — `marketing.seo.ai.systems.<код>`). */
const SEO_SIGNAL_AI_SYSTEMS = ['chatgpt', 'gemini', 'aio'];

const aiSystemLabel = (system: string | null): Record<string, string> => ({
  system:
    system && SEO_SIGNAL_AI_SYSTEMS.includes(system)
      ? `marketing.seo.ai.systems.${system}`
      : 'marketing.seo.ai.systems.all',
});

/** Вид прогона DataForSEO и причина отказа сметы — ключами подписи. */
const SEO_SIGNAL_RUN_KINDS = ['weekly', 'monthly', 'bootstrap'];
const SEO_SIGNAL_REFUSALS = [
  'cap',
  'max_run',
  'balance',
  'price_stale',
  'price_drift',
];

const seoRunKindLabel = (kind: string | null): string =>
  kind && SEO_SIGNAL_RUN_KINDS.includes(kind)
    ? `marketing.seo.budget.kinds.${kind}`
    : 'marketing.seo.budget.kinds.other';

const seoRefusalLabel = (reason: string | null): string =>
  reason && SEO_SIGNAL_REFUSALS.includes(reason)
    ? `marketing.seo.budget.refusals.${reason}`
    : 'marketing.seo.budget.refusals.other';

/**
 * Заголовок сигнала: `marketing.signals.<вид>.title` с подстановками из
 * `params` (числа — в формате языка). Незнакомый вид — общий заголовок с
 * кодом: новый сигнал сервера виден, а не пропадает.
 */
export const marketingSignalTitle = (
  signal: IMarketingSignal,
  lang?: string | null,
): IMarketingText => {
  const p = signal.params;
  const name = jsonText(p, 'name') ?? signal.entityName ?? MARKETING_DASH;
  switch (signal.kind) {
    case 'creative_fatigue':
      return {
        key: 'marketing.signals.creative_fatigue.title',
        params: { name },
      };
    case 'spend_no_leads':
      return {
        key: 'marketing.signals.spend_no_leads.title',
        params: { name },
        labels: levelLabel(jsonText(p, 'level')),
      };
    case 'cpl_spike': {
      const channelKey = marketingChannelLabelKey(
        signal.channel ?? marketingChannelFromKey(jsonText(p, 'channel') ?? ''),
      );
      return jsonText(p, 'name') || !channelKey
        ? {
            key: 'marketing.signals.cpl_spike.title',
            params: { subject: name },
          }
        : {
            key: 'marketing.signals.cpl_spike.title',
            params: {},
            labels: { subject: channelKey },
          };
    }
    case 'tracking_gap': {
      const pct = marketingJsonNumber(p, 'lost_pct');
      return {
        key: 'marketing.signals.tracking_gap.title',
        params: {
          pct:
            pct === null ? MARKETING_DASH : formatMarketingPct(pct / 100, lang),
        },
      };
    }
    case 'coverage_drop': {
      const pct = marketingJsonNumber(p, 'share_pct');
      return {
        key: 'marketing.signals.coverage_drop.title',
        params: {
          pct:
            pct === null ? MARKETING_DASH : formatMarketingPct(pct / 100, lang),
        },
      };
    }
    case 'source_stale': {
      const source = sourceLabels(jsonText(p, 'source'), 'source');
      const hours = marketingJsonNumber(p, 'hours');
      return hours === null
        ? {
            key: 'marketing.signals.source_stale.titleNever',
            params: source.params,
            labels: source.labels,
          }
        : {
            key: 'marketing.signals.source_stale.title',
            params: {
              ...source.params,
              hours: formatMarketingCount(hours, lang),
            },
            labels: source.labels,
          };
    }
    case 'campaign_mapping_gap': {
      const platform = sourceLabels(jsonText(p, 'platform'));
      const pct = marketingJsonNumber(p, 'mapped_pct');
      return {
        key: 'marketing.signals.campaign_mapping_gap.title',
        params: {
          ...platform.params,
          pct:
            pct === null ? MARKETING_DASH : formatMarketingPct(pct / 100, lang),
        },
        labels: platform.labels,
      };
    }
    case 'demand_unserved':
      return {
        key: 'marketing.signals.demand_unserved.title',
        params: {
          name: jsonText(p, 'name') ?? jsonText(p, 'family') ?? name,
          impressions: formatMarketingCount(
            marketingJsonNumber(p, 'impressions'),
            lang,
          ),
        },
      };
    case 'demand_rising': {
      const family = jsonText(p, 'name') ?? jsonText(p, 'family') ?? name;
      const growth = signedPct(marketingJsonNumber(p, 'growth_pct'), lang);
      return growth === null
        ? {
            key: 'marketing.signals.demand_rising.titleNew',
            params: { name: family },
          }
        : {
            key: 'marketing.signals.demand_rising.title',
            params: { name: family, growth },
          };
    }
    case 'seo_position_drop': {
      const page = jsonText(p, 'page') ?? MARKETING_DASH;
      const query = jsonText(p, 'query');
      return query
        ? {
            key: 'marketing.signals.seo_position_drop.title',
            params: { page, query },
          }
        : {
            key: 'marketing.signals.seo_position_drop.titleNoQuery',
            params: { page },
          };
    }
    case 'budget_hypothesis':
      return marketingHypothesisTitle(signal);
    case 'sent_not_signed':
      // `n`, а не `count`: `count` включил бы у i18next подбор формы
      // множественного числа.
      return {
        key: 'marketing.signals.sent_not_signed.title',
        params: {
          name,
          n: formatMarketingCount(marketingJsonNumber(p, 'sent'), lang),
        },
        labels: levelLabel(jsonText(p, 'level')),
      };
    case 'leads_no_sales':
      return {
        key: 'marketing.signals.leads_no_sales.title',
        params: {
          name,
          n: formatMarketingCount(marketingJsonNumber(p, 'leads'), lang),
        },
        labels: levelLabel(jsonText(p, 'level')),
      };
    case 'page_conversion_drop': {
      // Путь страницы — подстановкой `{{- page}}`: «/» и «&» i18next иначе
      // экранировал бы.
      const page =
        jsonText(p, 'page') ?? jsonText(signal.metrics, 'page') ?? name;
      const drop =
        marketingJsonNumber(p, 'drop_pct') ??
        marketingJsonNumber(signal.metrics, 'drop_pct');
      return drop === null
        ? {
            key: 'marketing.signals.page_conversion_drop.titleNoDrop',
            params: { page },
          }
        : {
            key: 'marketing.signals.page_conversion_drop.title',
            params: { page, drop: formatMarketingPct(drop / 100, lang) },
          };
    }
    case 'serp_rank_drop': {
      const drop = marketingJsonNumber(p, 'drop_pct');
      return {
        key: 'marketing.signals.serp_rank_drop.title',
        params: {
          name,
          drop:
            drop === null
              ? MARKETING_DASH
              : formatMarketingPct(drop / 100, lang),
          n: formatMarketingCount(marketingJsonNumber(p, 'dropped'), lang),
        },
      };
    }
    case 'competitor_gain':
      return {
        key: 'marketing.signals.competitor_gain.title',
        params: {
          domain:
            jsonText(p, 'label') ??
            jsonText(p, 'domain') ??
            jsonText(signal.metrics, 'domain') ??
            MARKETING_DASH,
          name,
          points: formatMarketingPosition(
            marketingJsonNumber(p, 'points'),
            lang,
          ),
        },
      };
    case 'ai_visibility_drop': {
      // `share` / `prev_share` параметров — уже в процентах (12.5).
      const sharePct = marketingJsonNumber(p, 'share');
      const prev = marketingJsonNumber(p, 'prev_share');
      return {
        key: 'marketing.signals.ai_visibility_drop.title',
        params: {
          share:
            sharePct === null
              ? MARKETING_DASH
              : formatMarketingPct(sharePct / 100, lang),
          prev:
            prev === null
              ? MARKETING_DASH
              : formatMarketingPct(prev / 100, lang),
        },
        labels: aiSystemLabel(jsonText(p, 'system')),
      };
    }
    case 'seo_gap_new': {
      const family = jsonText(p, 'family') ?? signal.familySlug ?? null;
      const base = {
        n: formatMarketingCount(marketingJsonNumber(p, 'keywords'), lang),
        volume: formatMarketingCount(marketingJsonNumber(p, 'volume'), lang),
      };
      // Без курса (`family: none`, имя пустое) — подпись своя «без курса»,
      // а не «—» и не код.
      return !family || family === MARKETING_NONE_KEY || !jsonText(p, 'name')
        ? { key: 'marketing.signals.seo_gap_new.titleNone', params: base }
        : {
            key: 'marketing.signals.seo_gap_new.title',
            params: { ...base, name },
          };
    }
    case 'seo_budget_hold':
      return {
        key: 'marketing.signals.seo_budget_hold.title',
        params: {},
        labels: {
          kind: seoRunKindLabel(jsonText(p, 'kind')),
          reason: seoRefusalLabel(jsonText(p, 'reason')),
        },
      };
    case 'offline_retract_hold':
      // `n`, а не `count`: `count` включил бы у i18next подбор формы
      // множественного числа.
      return {
        key: 'marketing.signals.offline_retract_hold.title',
        params: {
          n: formatMarketingCount(marketingJsonNumber(p, 'count'), lang),
          limit: formatMarketingCount(marketingJsonNumber(p, 'limit'), lang),
        },
      };
    default:
      return { key: 'marketing.signals.other', params: { kind: signal.kind } };
  }
};

/** Сколько несопоставленных меток назвать в строке доказательств. */
export const MARKETING_SIGNAL_TOP_VALUES = 3;

/**
 * Запросы сигнала SEO (`metrics.top`: `{keyword, prev, now}`) одной
 * строкой: «kurs python 7 → 15, data science 12 → —». Пусто — `null`.
 */
const seoTopMovesOrNull = (
  metrics: MarketingJson,
  lang?: string | null,
): string | null => {
  const items = Array.isArray(metrics.top) ? (metrics.top as unknown[]) : [];
  const line = items
    .slice(0, MARKETING_SIGNAL_TOP_VALUES)
    .map((item) => {
      const row = marketingParseJson(item);
      const keyword = jsonText(row, 'keyword');
      if (!keyword) return null;
      const prev = marketingJsonNumber(row, 'prev');
      const now = marketingJsonNumber(row, 'now');
      const was =
        prev === null ? MARKETING_DASH : formatMarketingCount(prev, lang);
      const is =
        now === null ? MARKETING_DASH : formatMarketingCount(now, lang);
      return `${keyword} ${was} → ${is}`;
    })
    .filter((item): item is string => item !== null)
    .join(', ');
  return line || null;
};

/** Крупнейшие разрывы сигнала (`metrics.top`: `{keyword, volume}`). */
const seoTopGapsOrNull = (
  metrics: MarketingJson,
  lang?: string | null,
): string | null => {
  const items = Array.isArray(metrics.top) ? (metrics.top as unknown[]) : [];
  const line = items
    .slice(0, MARKETING_SIGNAL_TOP_VALUES)
    .map((item) => {
      const row = marketingParseJson(item);
      const keyword = jsonText(row, 'keyword');
      if (!keyword) return null;
      const volume = marketingJsonNumber(row, 'volume');
      return volume === null
        ? keyword
        : `${keyword} (${formatMarketingCount(volume, lang)})`;
    })
    .filter((item): item is string => item !== null)
    .join(', ');
  return line || null;
};

/**
 * Топ-запросы сигнала спроса (`metrics.top_queries`) одной строкой:
 * «frontend developer kurs (120), kurs front end (80)». Пусто — `null`.
 */
const topQueriesOrNull = (
  metrics: MarketingJson,
  lang?: string | null,
): string | null => {
  const list = Array.isArray(metrics.top_queries)
    ? (metrics.top_queries as unknown[])
    : [];
  const text = list
    .slice(0, MARKETING_SIGNAL_TOP_VALUES)
    .map((item) => {
      const row = marketingParseJson(item);
      const query = jsonText(row, 'query');
      const impressions = marketingJsonNumber(row, 'impressions');
      if (!query) return null;
      return impressions === null
        ? query
        : `${query} (${formatMarketingCount(impressions, lang)})`;
    })
    .filter((item): item is string => item !== null)
    .join(', ');
  return text || null;
};

/**
 * Короткая строка доказательств сигнала словами: какие числа его зажгли.
 * У выгорания — первая причина (остальные — счётчиком рядом): полный список
 * причин живёт в «Креативах». `null` — сказать нечего.
 */
export const marketingSignalEvidence = (
  signal: IMarketingSignal,
  lang?: string | null,
): IMarketingText | null => {
  const m = signal.metrics;
  const n = (key: string) => marketingJsonNumber(m, key);
  const prefix = `marketing.signals.${signal.kind}.evidence`;
  switch (signal.kind) {
    case 'creative_fatigue': {
      const [first] = marketingFatigueEvidence(
        marketingFatigueFromSignal(m),
        lang,
      );
      return first ?? null;
    }
    case 'spend_no_leads': {
      const spend = moneyOrNull(n('spend_7d'), lang);
      return pickText(prefix, [
        {
          name: 'full',
          params: {
            spend,
            platformLeads: countOrNull(n('platform_leads'), lang),
          },
        },
        { name: 'noPlatform', params: { spend } },
        { name: 'bare', params: {} },
      ]);
    }
    case 'cpl_spike':
      return pickText(prefix, [
        {
          name: 'full',
          params: {
            now: moneyOrNull(n('cpl_7d'), lang),
            before: moneyOrNull(n('cpl_28d'), lang),
            ratio: ratioOrNull(n('ratio'), lang),
          },
        },
        { name: 'bare', params: {} },
      ]);
    case 'tracking_gap':
      return pickText(prefix, [
        {
          name: 'full',
          params: {
            platform: countOrNull(n('platform_leads'), lang),
            touches: countOrNull(n('touches_all_meta'), lang),
            newLeads: countOrNull(n('crm_leads_new_leads'), lang),
            lost: pctOrNull(n('lost_share'), lang),
          },
        },
        { name: 'bare', params: {} },
      ]);
    case 'coverage_drop':
      return pickText(prefix, [
        {
          name: 'full',
          params: {
            known: countOrNull(n('known_channel'), lang),
            sales: countOrNull(n('sales'), lang),
            share: pctOrNull(n('share'), lang),
          },
        },
        { name: 'bare', params: {} },
      ]);
    case 'source_stale': {
      const at = jsonText(m, 'last_success_at');
      const when = at ? marketingStamp(at) || null : null;
      return pickText(prefix, [
        {
          name: 'full',
          params: { when, hours: countOrNull(n('hours'), lang) },
        },
        { name: 'never', params: {} },
      ]);
    }
    case 'campaign_mapping_gap': {
      const values = Array.isArray(m.unmapped_values)
        ? (m.unmapped_values as unknown[])
        : [];
      const top = values
        .slice(0, MARKETING_SIGNAL_TOP_VALUES)
        .map((item) => {
          const row = marketingParseJson(item);
          const value = jsonText(row, 'value');
          const count = marketingJsonNumber(row, 'count');
          if (!value) return null;
          return count === null
            ? value
            : `${value} (${formatMarketingCount(count, lang)})`;
        })
        .filter((item): item is string => item !== null)
        .join(', ');
      const base = {
        mapped: countOrNull(n('mapped_leads'), lang),
        leads: countOrNull(n('crm_leads'), lang),
        pct: pctOrNull(n('mapped_share'), lang),
      };
      return pickText(prefix, [
        { name: 'full', params: { ...base, top: top || null } },
        { name: 'noTop', params: base },
        { name: 'bare', params: {} },
      ]);
    }
    case 'demand_unserved':
    case 'demand_rising': {
      const impressions = countOrNull(n('impressions_28d'), lang);
      const top = topQueriesOrNull(m, lang);
      const prev = n('prev_impressions_28d');
      const growth = signedPct(n('growth_pct'), lang);
      // Впервые: в прошлых 28 днях показов не было — роста в процентах нет.
      if (prev === 0) {
        return pickText(prefix, [
          { name: 'new', params: { impressions, top } },
          { name: 'newNoTop', params: { impressions } },
          { name: 'bare', params: {} },
        ]);
      }
      const base = {
        impressions,
        prev: countOrNull(prev, lang),
        growth,
      };
      return pickText(prefix, [
        { name: 'full', params: { ...base, top } },
        { name: 'noTop', params: base },
        { name: 'bare', params: {} },
      ]);
    }
    case 'seo_position_drop':
      return pickText(prefix, [
        {
          name: 'full',
          params: {
            now: positionOrNull(n('position_7d'), lang),
            prev: positionOrNull(n('position_prev_7d'), lang),
            before: positionOrNull(n('position_before'), lang),
          },
        },
        { name: 'bare', params: {} },
      ]);
    case 'budget_hypothesis':
      return marketingHypothesisEvidence(signal, lang)[0] ?? null;
    case 'offline_retract_hold':
      return pickText(prefix, [{ name: 'bare', params: {} }]);
    case 'sent_not_signed': {
      const base = {
        pending: countOrNull(n('contracts_pending'), lang),
        lost: countOrNull(n('contracts_lost'), lang),
      };
      return pickText(prefix, [
        {
          name: 'full',
          params: { ...base, value: moneyOrNull(n('pending_value_pln'), lang) },
        },
        { name: 'noValue', params: base },
        { name: 'bare', params: {} },
      ]);
    }
    case 'leads_no_sales': {
      const contracts = countOrNull(n('contracts_sent'), lang);
      return pickText(prefix, [
        {
          name: 'full',
          params: { contracts, spend: moneyOrNull(n('spend'), lang) },
        },
        { name: 'noSpend', params: { contracts } },
        { name: 'bare', params: {} },
      ]);
    }
    case 'page_conversion_drop': {
      // Конверсия входа в заявку — малые доли: до двух знаков.
      const base = {
        now: rateOrNull(n('conversion'), lang),
        before: rateOrNull(n('prev_conversion'), lang),
      };
      return pickText(prefix, [
        {
          name: 'full',
          params: {
            ...base,
            entrances: countOrNull(n('entrances'), lang),
            prevEntrances: countOrNull(n('prev_entrances'), lang),
          },
        },
        { name: 'noEntrances', params: base },
        { name: 'bare', params: {} },
      ]);
    }
    case 'serp_rank_drop': {
      const base = {
        now: pctOrNull(n('sov'), lang),
        prev: pctOrNull(n('prev_sov'), lang),
      };
      return pickText(prefix, [
        { name: 'full', params: { ...base, top: seoTopMovesOrNull(m, lang) } },
        { name: 'noTop', params: base },
        { name: 'bare', params: {} },
      ]);
    }
    case 'competitor_gain': {
      const base = {
        now: pctOrNull(n('sov'), lang),
        prev: pctOrNull(n('prev_sov'), lang),
        own: pctOrNull(n('own_sov'), lang),
      };
      return pickText(prefix, [
        { name: 'full', params: { ...base, top: seoTopMovesOrNull(m, lang) } },
        { name: 'noTop', params: base },
        { name: 'bare', params: {} },
      ]);
    }
    case 'ai_visibility_drop':
      return pickText(prefix, [
        {
          name: 'full',
          params: {
            n: countOrNull(n('answers'), lang),
            prevN: countOrNull(n('prev_answers'), lang),
            citation: pctOrNull(n('citation_share'), lang),
          },
        },
        { name: 'bare', params: {} },
      ]);
    case 'seo_gap_new':
      return pickText(prefix, [
        { name: 'full', params: { top: seoTopGapsOrNull(m, lang) } },
        { name: 'bare', params: {} },
      ]);
    case 'seo_budget_hold':
      return pickText(prefix, [
        {
          name: 'full',
          params: {
            estimate: usdOrNull(n('estimate_usd'), lang),
            spent: usdOrNull(n('spent_usd'), lang),
            cap: usdOrNull(n('cap_usd'), lang),
          },
        },
        { name: 'bare', params: {} },
      ]);
    default:
      return null;
  }
};

/* ── Качество данных: источник лида и сопоставление кампаний ─────────── */

/** Источник лида Kommo, у которого есть своя подпись. */
export const MARKETING_LEAD_SOURCES = ['zadarma', 'test', 'none'] as const;

/**
 * Подпись источника лида Kommo: «Zadarma» — звонки, «Test» — формы сайта
 * (интеграция сайта так называется в Kommo), пусто — «не указан». Прочие —
 * как пришли (`null`).
 */
export const marketingLeadSourceLabelKey = (
  value: string | null | undefined,
): string | null => {
  const code = (value ?? '').trim().toLowerCase();
  if (!code) return 'marketing.quality.leadSources.none';
  if (code === 'zadarma') return 'marketing.quality.leadSources.zadarma';
  if (code === 'test') return 'marketing.quality.leadSources.test';
  return null;
};

/** Площадки, с кампаниями которых сопоставляются метки CRM. */
export const MARKETING_ALIAS_PLATFORMS = ['meta', 'google_ads'];

/** Поля метки, которые сопоставляются с кампанией (`CampaignAliasField`). */
export const MARKETING_ALIAS_FIELDS = ['utm_campaign', 'utm_id'];

/**
 * «Сопоставить с кампанией» у строки несопоставленного: кампания кабинета
 * не нашлась, площадка известна и платная, а метка — из поля, которое
 * сервер умеет сопоставлять (имя кампании кабинета — уже кампания).
 */
export const marketingCanMapCampaign = (
  row: IMarketingUnmappedValue,
): boolean =>
  row.campaignMissing &&
  MARKETING_ALIAS_PLATFORMS.includes(row.platform ?? '') &&
  MARKETING_ALIAS_FIELDS.includes(row.field) &&
  Boolean(row.value.trim());

/** Ключ строки несопоставленного — поле и метка. */
export const marketingUnmappedKey = (
  row: Pick<IMarketingUnmappedValue, 'field' | 'value'>,
): string => `${row.field}:${row.value}`;
